import * as Tone from 'tone';
import { TransportController, TRANSPORT_MIN_LOOP_MS } from './transport/index.js';
import { Deck, syncEnabledDeckCount } from '../voice/Deck.js';
import { voiceRegistry } from '../voice/VoiceRegistry.js';
import { PlayerPlayback } from './playback/player.js';
import { StreamingPlayer } from './playback/streamingPlayer.js';
import { StretchPlayerPlayback } from './playback/stretchPlayer.js';
import { MediaSessionController } from './MediaSessionController.js';
import { resolvePlaybackStrategy } from './playback/strategyResolver.js';
import { isTrackDownloadedSync } from '../offline/offlineAvailability.js';
import { hasLocalByteSource } from '../local/localByteSources.js';
import { isMultiplayerBoot } from '../utils/urlParams.js';
import { ParameterLockCoordinator } from './parameterLockCoordinator.js';
import { EffectsRack } from './effects/rack.js';
import { sanitizeMappings } from './effects/mappingManager.js';
import { ENGINE_REQUIREMENT, resolveModuleEngineRequirement } from './effects/index.js';
import { MAX_MODULES, isMobileDevice, setPlaybackState, GAIN_STAGE_CONSTRAINTS } from '../config/Constants.js';
import { AUDIO_PERFORMANCE_PRESETS } from '../config/audioPerformance.js';
import { ensureSilentAudioUnlock } from './SilentAudioUnlock.js';
import { createSourceEngineHost } from './sourceEngineHost.js';
import { syncCoordinator } from '../sync/SyncCoordinator.js';
import { normalizeLoopRange } from './playback/loopRange.js';
import {
  resolveLoopEnabledFromTrackData,
  resolveLoopRangeSecFromTrackData,
} from 'entangled-worlds-orbiters-shared/clock/track-metadata';
import { INPUT_GAIN_PARAM, PREMIX_PARAM } from './gainStageParams.js';
import { getManualAudioOffsetMs } from '../config/audioOffset.js';
import { enforceStereo } from './audioNodeUtils.js';
import { createLevelTap } from './levelTap.js';
import { isDebugSyncLoggingEnabled, isDriftCorrectionDisabled } from '../sync/debugSync.js';
import {
  foldDriftMs,
  medianOf,
  decideDriftAction,
  DRIFT_SAMPLE_WINDOW,
  DRIFT_SAMPLE_INTERVAL_MS,
  DRIFT_SAMPLE_PARKED_INTERVAL_MS,
  DRIFT_REALIGN_CONSECUTIVE,
  DRIFT_REALIGN_COOLDOWN_MS,
} from '../sync/drift.js';
// The source tag the display mirror stamps on its own `sync-bpm` writes, so the
// control→setTempo bridge can tell its own echo apart from a real user/MIDI edit and not loop.
// (`sync-bpm` is bidirectional, so ParameterManager notifies even the writing controller — the
// source tag, not controller-identity skip, is what breaks the loop.)
import { SYNC_BPM_DISPLAY_SOURCE } from './syncBpmDisplaySource.js';
import { createDebugLog } from '../utils/debugLog.js';

const AXES = ['x', 'y', 'z'];
const DEFAULT_DIMENSION_ID = 'EW::I';
const DEFAULT_DIMENSION_ORDER = ['EW::I', 'EW::II', 'EW::III'];
// The two user gain stages, from their one declaration (`gainStageParams.js`). INPUT_GAIN_PARAM is
// the player's trim AHEAD of all processing — distinct from `normalizationGain`, which is the track's
// own loudness correction from backend analysis and is not a control at all. PREMIX_PARAM is its
// counterpart after the chain.
// Both gain controls and the level meter share ONE dB axis (see GAIN_STAGE_CONSTRAINTS).
const GAIN_MIN_DB = GAIN_STAGE_CONSTRAINTS.min;
const GAIN_MAX_DB = GAIN_STAGE_CONSTRAINTS.max;
const PARAM_DEBUG_FLAG = '__DEBUG_PARAM_ROUTE';
// Engine lifecycle running commentary — which playback engine was chosen, when a reload finished,
// what normalization gain was applied. Every line of it describes the engine working, so it is off
// unless `window.__DEBUG_ENGINE = true`. Failures in the same paths stay unconditional.
const debugEngine = createDebugLog('__DEBUG_ENGINE', '[AudioEngineAdapter]');
// The buffered-reload watchdog: generous (the attempt includes a full track
// download on a possibly slow mobile network), but finite — a hung await in
// the chain must surface as a reported failure, never as silence.
const BUFFERED_RELOAD_WATCHDOG_MS = 5 * 60 * 1000;
const RELOAD_WATCHDOG_TIMEOUT = Symbol('buffered-reload-timeout');
// Per-stage input headroom. The orbiter runs 3 dimension stages (EW::I/II/III)
// in SERIES. The original -6 dB per stage (at the deck channel, end-of-stage) both
// over-attenuated (3 × -6 = -18 dB) and gave the processors no input headroom. Instead,
// apply a small cut at the INPUT of each stage: every processor then has headroom and
// can't clip internally, and the cuts distribute the deck headroom across the chain
// rather than lumping it at the output. -1 dB per stage plus a small -0.5 dB final output
// trim ≈ 3 dB total, landing a -6 dBFS reference at ~-9 dBFS (no gain) / ~-3 dBFS (max
// premix +6 dB) — verified with offline analysis (pink-noise in/out RMS + LUFS).
const STAGE_HEADROOM_DB = -1;
// Final global output trim, applied once at masterGain (after the per-stage input headroom).
const OUTPUT_HEADROOM_DB = -0.5;
const dbToLinear = (db) => (Tone.dbToGain ? Tone.dbToGain(db) : Math.pow(10, db / 20));

function shouldDebugParamRoute() {
  try {
    if (typeof window === 'undefined') return false;
    if (!(PARAM_DEBUG_FLAG in window)) {
      Object.defineProperty(window, PARAM_DEBUG_FLAG, {
        value: false,
        writable: true,
        configurable: true,
        enumerable: false,
      });
    }
    return Boolean(window[PARAM_DEBUG_FLAG]);
  } catch (_) {
    return false;
  }
}

function debugParamRoute(message, payload = {}) {
  if (!shouldDebugParamRoute()) return;
  try {
    console.debug(`[AudioParamRoute] ${message}`, payload);
  } catch (_) {}
}

function shouldDebugEngineStacks() {
  try {
    if (typeof window === 'undefined') return false;
    return Boolean(window.__DEBUG_ENGINE_STACKS);
  } catch (_) {
    return false;
  }
}

function debugEngineStacks(message, payload = {}) {
  if (!shouldDebugEngineStacks()) return;
  try {
    console.debug(`[AudioEngineStacks] ${message}`, payload);
  } catch (_) {}
}

function cloneModuleConfig(config = {}, fallbackDimensionId = null) {
  const range = config?.range || {};
  return {
    effectId: config?.effectId ?? null,
    moduleId: config?.moduleId ?? null,
    inputParamId: config?.inputParamId ?? null,
    range: {
      min: Number.isFinite(range.min) ? Number(range.min) : null,
      max: Number.isFinite(range.max) ? Number(range.max) : null,
      equilibrium: Number.isFinite(range.equilibrium ?? range.init)
        ? Number(range.equilibrium ?? range.init)
        : null,
    },
    settings: config?.settings ? { ...config.settings } : undefined,
    mappings: sanitizeMappings(config?.mappings),
    dimensionId: config?.dimensionId ?? fallbackDimensionId ?? null,
    dimensionLabel: config?.dimensionLabel ?? null,
    controlNormalized: Number.isFinite(config?.controlNormalized)
      ? Math.min(1, Math.max(0, Number(config.controlNormalized)))
      : null,
  };
}

function cloneRackConfig(config = {}, fallbackDimensionId = null) {
  // Do NOT enforce MAX_MODULES here; cloning should preserve all modules.
  // Limiting to MAX_MODULES must happen AFTER per-dimension filtering.
  const modules = Array.isArray(config.modules)
    ? config.modules.map((module) => cloneModuleConfig(module, fallbackDimensionId))
    : [];

  return {
    dimensionId: config?.dimensionId ?? fallbackDimensionId ?? null,
    dimensionLabel: config?.dimensionLabel ?? null,
    modules,
  };
}

function shouldPrimeDimensionConfig(config) {
  return Boolean(config && (config.x || config.y || config.z));
}

export class AudioEngineAdapter {
  constructor({ trackData, engineConfig, userManager, performanceProfile, transport, outputNode, deck, eventBus, loadProgress, multiplayer } = {}) {
    this.trackData = trackData;
    // Is this voice part of a shared session? Read from the frozen boot fact, never from live
    // room state: on the inline-roster path a voice builds its engine (and preloads) before the
    // room client exists, so live state would answer "no" and stay wrong for this voice's whole
    // life. Fixed for the voice's lifetime by design — a session that ENDS must not downgrade a
    // still-playing voice to streaming and re-download it mid-flight. Overridable for tests.
    this._multiplayer = multiplayer ?? isMultiplayerBoot();
    // Set once this engine has actually initialized AND preloaded its audio. The boot flow owns
    // the write; readers use it instead of assuming a stored engine is a working one.
    this.audioReady = false;
    // This voice's load-progress reporter; audio download counters report through it
    // (per-tile in the multi realm) instead of the one global overlay. Null → the playback layer
    // falls back to the legacy window.updateLoadingProgress hook (single-orbiter unchanged).
    this._loadProgress = loadProgress ?? null;
    this.playbackTrackData = trackData?.track ?? trackData ?? null;
    this.engineConfig = engineConfig || {};
    this.userManager = userManager;
    // Multi-orbiter seams (decision 0001). Both default to today's behavior, so a
    // single-orbiter instance is unchanged. A multi-orbiter owner can inject a SHARED transport
    // (so N voices run off one shared-package Transport instead of each owning its own) and a shared
    // output node (so N voices mix into one master bus instead of each hitting Tone.Destination).
    this._injectedTransport = transport ?? null;
    this._outputNode = outputNode ?? null;
    // The per-voice event bus the count-in snapshot mirrors onto (the React Transport
    // surface subscribes to the SAME bus). Defaults to `window` so single-orbiter is byte-identical; a
    // multi tile injects its own EventTarget so one voice's count-in doesn't drive another's UI.
    this._eventBus = eventBus ?? (typeof window !== 'undefined' ? window : null);
    // This voice's DECK — the one owner of its sync/warp flags, tempo, meter, grid, and beat clock.
    // Injected by the session (registered on the voice record); a deck-less path (some tests)
    // gets a private solo-semantics deck so grid/meter reads keep working.
    this.deck = deck ?? new Deck({ voiceId: null, collection: false });
    this.performanceProfile = this.#resolvePerformanceProfile(performanceProfile);
    this.performanceProfileKey = this.performanceProfile?.key ?? 'MID';
    this.safeRampSeconds = this.#computeSafeRampSeconds();
    this._speedControlDisabled = false;
    this._speedControlDisableReason = null;
    // Engine-requiring modules present but not runnable on the current
    // (streaming) backend: their parameters are locked until an explicit
    // buffered unlock. All ParameterManager dimension locks go through the
    // one coordinator (see parameterLockCoordinator.js) — its manager getter
    // is lazy because userManager can be attached after construction.
    // Initialized BEFORE the first #resolveDecodeStrategy below, which runs
    // the lock sync.
    this._engineFeaturesBlocked = false;
    this._emittedEngineFeaturesBlocked = false;
    this._parameterLocks = new ParameterLockCoordinator({
      getManager: () => this.userManager,
    });

    this.axisOrder = [...AXES];
    this.transport = this._injectedTransport ?? new TransportController();
    this.playback = this.#createPlayback(this.#resolveDecodeStrategy(this.performanceProfile));
    this.mediaSession = new MediaSessionController(this);

    this.normalizationGain = null;
    this.inputLevelGain = null;
    this.masterGain = null;
    this.limiter = null;
    this.masterMeter = null;
    this.bodyLevelGain = null;
    // Level probes for the gain rail's meter: one per user-controlled gain stage, each reading
    // the signal as it leaves that stage. Peak (not RMS) so a transient that would clip shows,
    // and unheld — the meter component owns its own release curve.
    this._inputLevelTap = null;
    this._outputLevelTap = null;
    // The node `inputLevelGain` currently feeds, so a rewire can drop that one edge without
    // also detaching the level tap hanging off the same output.
    this._inputStageTarget = null;

    // This voice's source-level engine host (granular today; a companion family attaches at the
    // same seam). The host owns graph construction — context, mix-bus connect, dry leg — plus
    // refcounted lifetime and observation; control modules acquire, visual layers peek/observe.
    // Wired as live reads because the mix bus and buffer exist only after initialize().
    this._sourceEngines = createSourceEngineHost({
      getContext: () => Tone.getContext?.()?.rawContext ?? Tone.context?.rawContext ?? null,
      getMixBus: () => this.getSourceMixBus(),
      connect: (outputNode, mixBus) => Tone.connect(outputNode, mixBus),
      getBuffer: () => this.getDecodedAudioBuffer(),
      getPositionMs: () => this.getCurrentPositionMs(),
      isPlaying: () => this.isPlaying(),
      setDryLevel: (level) => this.setSourceDryLevel(level),
      getWorkletSurface: () => this.playback?.getGranularWorkletSurface?.() ?? null,
    });

    this.effectRacks = { x: null, y: null, z: null };

    // Rack-effect slot observers (the visual seam): every rack this adapter
    // builds relays its slot lifecycle here — see observeEffectSlots().
    this._effectSlotObservers = new Set();

    this._dimensionChains = new Map();
    this._dimensionOrder = [...DEFAULT_DIMENSION_ORDER];
    this._dimensionIds = new Set([DEFAULT_DIMENSION_ID]);
    this._effectsMeta = { activeDimensionId: null };
    this.effectsConfigByDimension = new Map();
    this._lastDiscreteValues = {};
    this._subscribedParams = new Set();
    this._subscriptionsRegistered = false;
    // The loop is ONE thing: a range (stored by the playback sink, kept even while disengaged) plus
    // this enabled flag. Whether it is ENGAGED is never stored — see `_resolveLoopEngagement`.
    // THE loop: the range the user drew and whether it is on. One owner. The playback sink and the
    // transport are mirrors this writes to — never where the loop lives, or a sink swap (which
    // disposes the range) would be able to lose or resurrect it.
    this._loop = null;
    this._loopEnabled = false;
    // Where the next start begins, when the user has chosen it (the play-from cursor, or a seek that
    // landed while nothing was playing). `{ rawMs, landedMs }`: the position ASKED for, and where the
    // playhead actually came to rest — this device's output-latency lead separates the two. Null =
    // start from wherever playback left off.
    this._startPosition = null;
    this._cosmicManager = null;
    this._cosmicLfoCache = { x: null, y: null, z: null };
    this._playbackStopSubscription = null;
    this._playbackBufferingSubscription = null;
    this._bufferingState = {
      isBuffering: false,
      bufferedAheadMs: 0,
      readyState: 0,
      source: null,
      timestamp: 0,
    };
    this._playbackState = 'stopped';
    this._playbackStateListeners = new Set();
    this._pendingDecodeStrategy = null;
    // NOT reset here: the first #resolveDecodeStrategy already ran above (playback
    // construction) and stored the resolution — nulling it would disarm the
    // unlock feasibility guard until the next resolve.
    this._playbackStrategyResolution = this._playbackStrategyResolution ?? null;
    // "Unlock speed": sticky user override of the adaptive streaming decision + its in-flight guard.
    this._forcePrebuffer = false;
    this._bufferedReloadPromise = null;
    this._bufferedReloadToken = null;
    this._pendingQuantizedStart = null;
    // A seek armed to fire on the next bar so it lands in phase (joined + playing only).
    this._pendingQuantizedSeek = null;
    // Room mode only: the transport event bridge (publish start/stop + the ownership check) and a
    // stop armed to fire on a shared beat. Both null outside a room — every path is then unchanged.
    this._roomTransport = null;
    this._pendingRoomStop = null;
    // ── shared-timecode drift ────────────────────────────────────────────────
    // Alignment used to be a one-shot at start; after that each client free-ran on its own audio
    // clock, so any per-device rate error integrated without a ceiling. These hold the periodic
    // measurement (expected-from-the-shared-beat vs actual playhead) and the correction ladder it
    // drives. All inert without a joined shared clock — solo playback never starts the sampler.
    this._driftTimer = null;
    this._driftSamples = [];
    // null = "no previous observation". A zero here would read as a page-lifetime timestamp, so the
    // FIRST tick of a voice built any time after boot would look like a suspension gap and trigger a
    // hard re-align before a single sample had been taken.
    this._driftLastSampleAt = null;
    this._driftScheduledDelayMs = null;
    this._driftLastPositionMs = null;
    this._driftLastSampleMs = null;
    this._driftMedianMs = null;
    this._driftOverCount = 0;
    // The signed rate trim the ladder holds. Composed into `_syncWrapPlaybackRate`'s target — that
    // method stays the ONE writer of the playback rate; nothing here ever calls setPlaybackRate.
    this._driftTrim = 0;
    // −Infinity, not 0: the clock here is page-lifetime ms, so a zero would read as "corrected just
    // now" and mute the first re-align of every voice for the page's first few seconds.
    this._driftLastRealignAt = -Infinity;
    this._pendingDriftRealign = null;
    // Monotonic seek generation — see `_seekNow`. Bumped by every seek and by anything that
    // invalidates one in flight, so an already-fired seek can still be stopped mid-await.
    this._seekEpoch = 0;
    // Tail of the per-voice seek queue — see `_seekNow`.
    this._seekChain = null;
    // Bumped by every loop edit. Paired with the deck's projection generation, it is the token a
    // scheduled re-align re-checks at fire time — the projection it was armed against must still hold.
    this._loopGeneration = 0;
    // Room mode only: the LOOP event bridge (publish enable/range + the ownership check). Null outside
    // a room; a user loop edit then publishes to the room and a remote edit applies back through here.
    this._roomLoop = null;
    // The quantized-start count-in snapshot (the wait between Play and the launch bar). Read
    // by the React Transport via the `transport` surface; mirrored on the `orbiters:quantize-countin`
    // window event. Inactive until a delayed start is scheduled.
    this._countInState = { active: false };
    // Seed the deck from this voice's loaded trackData (two-phase: the deck was constructed at voice
    // registration, before audio existed) — its native tempo, meter, and grid marker; a collection
    // deck still riding its native also seeds its transport tempo (ratio 1 — nothing audibly changes
    // at load, and the header number is honest from first paint, whatever the registry size was when
    // this tile constructed).
    this.deck.seedFromTrackData(this.trackData);
    // The deck's own playback position feeds its beat clock (`deck.clock()`) when it isn't on the
    // shared clock — the metronome and any per-player beat consumer read the deck, not the engine.
    this.deck.setPositionSource(() => ({
      playing: this.isPlaying?.() === true,
      positionMs: this.getCurrentPositionMs?.(),
    }));
    // The "BPM" number IS `deck.tempo` — the deck's continuous transport tempo (master-driven while
    // synced, its own while unsynced, held across sync toggles). ONE display source; the deck's
    // change stream below keeps the readout current. Editing the number BY ANY METHOD (type/wheel/
    // keypad/MIDI) converges on `setRawValue('sync-bpm', …)`, so ONE subscriber catches them all and
    // routes through `deck.setTempo` — synced proposes the master (the coordinator's single gate
    // stays the authority), unsynced sets the deck's own transport tempo (session-only; the track's
    // persisted tempo is its NATIVE tempo, edited via the kit panel). It skips the display mirror's
    // own echo (tagged SYNC_BPM_DISPLAY_SOURCE) and the subscribe replay, so it never loops.
    this._syncBpmBridgeSub = null;
    if (typeof this.userManager?.subscribe === 'function') {
      const controller = {
        onParameterChanged: (_name, value, _dim, metadata) => {
          // ParameterManager replays the current/default value to a new subscriber immediately
          // (reason:'subscribe'); driving setTempo from that would clobber a live master tempo with the
          // sync-bpm default when a voice inits while a session is already running. Ignore the replay.
          if (metadata?.reason === 'subscribe') return;
          if (metadata?.sourceController === SYNC_BPM_DISPLAY_SOURCE) return; // our own display echo
          const bpm = Number(value);
          if (Number.isFinite(bpm) && bpm > 0) this.deck.setTempo(bpm);
        },
      };
      this.userManager.subscribe(controller, 'sync-bpm');
      this._syncBpmBridgeSub = () => {
        try { this.userManager.unsubscribe(controller, 'sync-bpm'); } catch (_) {}
      };
    }
    this._deckSubscription = this.deck.onChange((snapshot, reason) => {
      // Which clock this voice follows just changed — a trim measured against the old one is void.
      // Dropped BEFORE the re-fan below so the rate that goes out is the clean baseline.
      if (reason === 'sync-status') {
        this._resetDriftCorrection();
        this._refreshDriftSampler(); // this voice may have just gained or lost a shared phase
      }
      if (reason === 'bpm' || reason === 'sync-status') {
        this._syncWrapPlaybackRate({ immediate: false });
        this._refreshSyncBpmReadout();
        this._refreshTrackBpmReadout();
      }
      // Cancel any pending quantized start if sync was just disabled — the
      // quantization target (shared session beat) no longer exists. A beat-scheduled
      // room stop loses its grid the same way; stop immediately rather than never.
      if (reason === 'sync-status' && syncCoordinator?.isEnabled !== true) {
        this._cancelPendingQuantizedStart();
        if (this._pendingRoomStop) {
          this._cancelPendingRoomStop();
          void this.stop({ roomRemote: true });
        }
      }
      // Position alignment only when NOT actively playing.
      // During playback, rate control alone keeps the track aligned.
      // Seeking a streaming HTMLMediaElement mid-playback invalidates buffered
      // data and causes progressive freezes when events arrive faster than the
      // browser can re-buffer.
      if (
        syncCoordinator?.isEnabled === true &&
        !this.playback?.isPlaying?.() &&
        (reason === 'grid-marker' || reason === 'grid-start' || reason === 'bpm' || reason === 'sync-status')
      ) {
        this._alignWrapPlaybackPosition({ force: false }).catch(() => {});
      }
    });
    // Boot: the readout starts on the deck's tempo (a collection tile's own native; solo the master —
    // the same value the old master mirror wrote on the coordinator's init emission).
    this._refreshSyncBpmReadout();
    this._refreshTrackBpmReadout();
    this._handlePlaybackStopped = this._handlePlaybackStopped.bind(this);
    this._handlePlaybackBuffering = this._handlePlaybackBuffering.bind(this);
    this._initialized = false;

    this.refreshCosmicLfoCache();
    this._primeDimensionIdsFromStacks(this.engineConfig?.stacks);
  }

  // The resolver owns the WHOLE decision (final sink, engine-requirement
  // block, speed lock — incl. the URL flags and the sticky unlock override);
  // this method only feeds it the adapter's state and APPLIES the verdict.
  #resolveDecodeStrategy(profile = this.performanceProfile, effectsConfig = this.engineConfig?.effects) {
    const resolution = resolvePlaybackStrategy({
      profile,
      effectsConfig,
      trackData: this.playbackTrackData,
      forceBuffered: this._forcePrebuffer === true,
      multiplayer: this._multiplayer,
      // A synchronous read on purpose: the sink has to be decided while the voice is being built,
      // and there is nothing to await against. A stale "no" costs a streamed load that would have
      // been buffered; a stale "yes" costs a buffered load that falls back to the network. Neither
      // breaks, which is what makes the snapshot an acceptable answer here.
      offlineAvailable: isTrackDownloadedSync(this.playbackTrackData),
      // A file on this machine has no URL to stream from; the registry is synchronous for the
      // same reason the offline snapshot is.
      localBytes: hasLocalByteSource(this.playbackTrackData?.trackId),
    });
    this._playbackStrategyResolution = resolution || null;
    const strategy = resolution?.sink ?? 'stream';

    // Engine-requiring modules that can't run on the decided sink: their
    // parameters lock and the UI offers the buffered unlock where it is sane.
    this._engineFeaturesBlocked = resolution?.requirementBlocked === true;
    this.#setSpeedControlDisabled(resolution?.shouldLockSpeed === true, {
      reason: resolution?.speedLockReason ?? null,
      strategy,
      prebufferThresholdSec: resolution?.prebufferThresholdSec ?? resolution?.longFormThresholdSec ?? null,
      source: 'strategy-resolution',
    });
    // Runs on EVERY resolve (not only on lock-state flips): the effects config
    // may have changed which parameters need locking while the lock states
    // themselves stayed put.
    this.#syncParameterLocks();
    if (this._engineFeaturesBlocked !== this._emittedEngineFeaturesBlocked) {
      this._emittedEngineFeaturesBlocked = this._engineFeaturesBlocked;
      this.#emitControlLockEvent({
        disabled: this._speedControlDisabled,
        reason: this._speedControlDisableReason,
        strategy,
        prebufferThresholdSec: resolution?.prebufferThresholdSec ?? null,
        source: 'strategy-resolution',
      });
    }

    return strategy;
  }

  /** Feasibility + lock state for UI decisions (unlock affordances). */
  getPlaybackStrategyInfo() {
    const resolution = this._playbackStrategyResolution || null;
    return {
      strategy: this.playback?.getDecodeStrategy?.() ?? null,
      bufferedFeasible: Boolean(resolution?.bufferedFeasible),
      requirementBlocked: Boolean(resolution?.requirementBlocked),
      engineFeaturesBlocked: this._engineFeaturesBlocked === true,
      speedControlDisabled: this._speedControlDisabled === true,
      durationSec: resolution?.durationSec ?? null,
      predictedPrebufferSec: resolution?.predictedPrebufferSec ?? null,
      mobile: Boolean(resolution?.mobile),
    };
  }

  #setSpeedControlDisabled(disabled, {
    reason = null,
    strategy = null,
    prebufferThresholdSec = null,
    source = 'runtime',
  } = {}) {
    const next = Boolean(disabled);
    if (this._speedControlDisabled === next && this._speedControlDisableReason === reason) {
      return;
    }
    this._speedControlDisabled = next;
    this._speedControlDisableReason = next ? reason : null;

    // Mobile speed lock: enforcement lives at the playback-engine sink — the ONE gate every rate write
    // (knob, transport warp, effect automation) passes through. Pushing the flag here means no
    // transport-driven tempo change can bypass the lock on mobile.
    this.playback?.setSpeedControlLocked?.(next);

    // Warp's only job is that (now-blocked) stretch — force it off + lock the control on the deck so
    // the UI can grey it out, and so the metronome/grid stay on the deck's native tempo, not a
    // transport tempo the audio isn't playing.
    this.deck?.setSpeedLocked?.(next);

    // Parameter locks are applied by the one shared owner (speed lock and the
    // engine-feature block can target the same dimensions).
    this.#syncParameterLocks();

    this.#emitControlLockEvent({ disabled: next, reason, strategy, prebufferThresholdSec, source });
  }

  #emitControlLockEvent({ disabled, reason = null, strategy = null, prebufferThresholdSec = null, source = 'runtime' }) {
    if (typeof document === 'undefined') return;
    document.dispatchEvent(new CustomEvent('orbiters:speed-control-lock', {
      detail: {
        disabled: Boolean(disabled),
        reason: disabled ? reason : null,
        strategy: strategy || this.playback?.getDecodeStrategy?.() || null,
        prebufferThresholdSec,
        // Engine-requiring modules blocked on the current backend (distinct
        // from the mobile speed lock — either can be active without the other).
        engineFeaturesBlocked: this._engineFeaturesBlocked === true,
        source,
        timestamp: typeof performance !== 'undefined' ? performance.now() : Date.now(),
      },
    }));
  }

  /**
   * Feed both lock causes to the coordinator (the one owner of ParameterManager
   * dimension locks — see parameterLockCoordinator.js). Runs on every strategy
   * resolve and effects-config install: the causes may be unchanged while the
   * TARGETS moved with the config.
   */
  #syncParameterLocks() {
    this._parameterLocks.setCauses({
      'speed-lock': this._speedControlDisabled ? this.#getSpeedControlTargets() : [],
      'engine-block': this._engineFeaturesBlocked ? this.#getEngineFeatureTargets() : [],
    });
  }

  /**
   * The ONE walk over the effects configuration: per axis+dimension, the
   * modules matching `match` — from the resolved per-dimension map when
   * populated, else the raw config (early init), where the dimension id comes
   * from the first matched module. Every target enumeration (speed effects,
   * engine-requiring modules, tempo-managed modules) is a predicate + mapping
   * over this.
   * @param {(moduleConfig: object) => boolean} match
   * @returns {{ axis: string, dimensionId: string, modules: object[] }[]}
   */
  #collectAxisModuleTargets(match) {
    const targets = [];
    if (this.effectsConfigByDimension && this.effectsConfigByDimension.size > 0) {
      this.effectsConfigByDimension.forEach((dimConfig, dimensionId) => {
        for (const axis of AXES) {
          const modules = Array.isArray(dimConfig?.[axis]?.modules) ? dimConfig[axis].modules : [];
          const matched = modules.filter(match);
          if (matched.length) {
            targets.push({ axis, dimensionId, modules: matched });
          }
        }
      });
      return targets;
    }

    const effectsConfig = this.engineConfig?.effects || {};
    for (const axis of AXES) {
      const axisConfig = effectsConfig[axis];
      const modules = Array.isArray(axisConfig?.modules) ? axisConfig.modules : [];
      const matched = modules.filter(match);
      if (matched.length) {
        targets.push({
          axis,
          dimensionId: matched[0].dimensionId || axisConfig?.dimensionId || DEFAULT_DIMENSION_ID,
          modules: matched,
        });
      }
    }
    return targets;
  }

  /**
   * {axis, dimensionId, effectIds} for every axis+dimension carrying a
   * speed-related effect (what the mobile speed lock freezes).
   */
  #getSpeedControlTargets() {
    const SPEED_EFFECT_IDS = new Set(['tone.tempoPitch', 'tone.timeReverse']);
    return this.#collectAxisModuleTargets((moduleConfig) => SPEED_EFFECT_IDS.has(moduleConfig?.effectId))
      .map(({ axis, dimensionId, modules }) => ({
        axis,
        dimensionId,
        effectIds: Array.from(new Set(modules.map((moduleConfig) => moduleConfig.effectId))),
      }));
  }

  getSpeedControlTargets() {
    return this.#getSpeedControlTargets();
  }

  /**
   * {axis, dimensionId} for every axis+dimension carrying a module that needs
   * a buffered engine (granular, stretch/pure-pitch) — what the engine-feature
   * block locks while the voice streams.
   */
  #getEngineFeatureTargets() {
    const needsEngine = (moduleConfig) => {
      const effectId = moduleConfig?.effectId ?? null;
      if (!effectId) return false;
      return resolveModuleEngineRequirement(effectId, moduleConfig?.moduleId ?? null)
        !== ENGINE_REQUIREMENT.STREAM_SAFE;
    };
    return this.#collectAxisModuleTargets(needsEngine)
      .map(({ axis, dimensionId }) => ({ axis, dimensionId }));
  }

  hasSpeedControlTarget(axis, dimensionId = null) {
    if (!axis || !AXES.includes(axis)) return false;
    const targets = this.#getSpeedControlTargets();
    return targets.some((target) => {
      if (target.axis !== axis) return false;
      if (!dimensionId) return true;
      return target.dimensionId === dimensionId;
    });
  }

  hasTempoManagedSpeedTarget(axis, dimensionId = null) {
    if (!axis || !AXES.includes(axis)) return false;
    const targets = this.getTempoManagedTargets();
    return targets.some((target) => {
      if (target.axis !== axis) return false;
      if (dimensionId && target.dimensionId !== dimensionId) return false;
      return true;
    });
  }

  getTempoManagedTargets() {
    const targets = [];
    // Historical semantics: the FIRST tempoPitch module on the axis decides —
    // if it isn't a tempo* module, the axis doesn't count even if a later
    // module were one.
    for (const { axis, dimensionId, modules } of this.#collectAxisModuleTargets(
      (moduleConfig) => moduleConfig?.effectId === 'tone.tempoPitch',
    )) {
      const tempoModule = modules[0];
      const moduleId = typeof tempoModule.moduleId === 'string' ? tempoModule.moduleId : '';
      if (!moduleId.startsWith('tempo')) continue;
      targets.push({
        axis,
        dimensionId,
        moduleId: moduleId || null,
        range: tempoModule.range ? { ...tempoModule.range } : null,
      });
    }
    return targets;
  }

  #createPlayback(strategy = 'stream') {
    const useStretchEngine = strategy === 'stretch';
    if (useStretchEngine && !this._loggedStretchEngine) {
      this._loggedStretchEngine = true;
      debugEngine('time-stretch engine enabled for this voice (speed changes keep pitch)');
    }
    const PlaybackCtor = useStretchEngine
      ? StretchPlayerPlayback
      : (strategy === 'prebuffer' ? PlayerPlayback : StreamingPlayer);
    const playback = new PlaybackCtor({
      trackData: this.playbackTrackData,
      userManager: this.userManager,
      effectOrder: this.axisOrder,
      performanceProfile: this.performanceProfile,
      onLoadProgress: this._loadProgress ? (message) => this._loadProgress.setMessage(message) : null,
    });
    // A freshly created / swapped engine inherits the current speed lock, so a
    // prebuffer→stream swap on mobile can never briefly reopen the rate path.
    playback.setSpeedControlLocked?.(this._speedControlDisabled);
    return playback;
  }

  async #swapPlayback(strategy) {
    const currentStrategy = typeof this.playback?.getDecodeStrategy === 'function'
      ? this.playback.getDecodeStrategy()
      : null;
    if (currentStrategy === strategy) {
      return;
    }

    // Unsubscribe from old playback listeners first
    if (this._playbackStopSubscription) {
      try {
        this._playbackStopSubscription();
      } catch (_) {}
      this._playbackStopSubscription = null;
    }
    if (this._playbackBufferingSubscription) {
      try {
        this._playbackBufferingSubscription();
      } catch (_) {}
      this._playbackBufferingSubscription = null;
    }

    // CRITICAL: Ensure old playback is fully stopped and disposed before creating new one
    // This prevents duplicate audio playback when swapping between streaming and prebuffer modes
    const oldPlayback = this.playback;
    if (oldPlayback) {
      // First, ensure playback is stopped (with proper await to let audio fade out)
      if (typeof oldPlayback.triggerStop === 'function') {
        try {
          await oldPlayback.triggerStop();
        } catch (error) {
          console.warn('[AudioEngineAdapter] Failed to stop old playback during swap:', error);
        }
      }

      // Then dispose all resources
      if (typeof oldPlayback.dispose === 'function') {
        try {
          oldPlayback.dispose();
        } catch (error) {
          console.warn('[AudioEngineAdapter] Failed to dispose old playback during swap:', error);
        }
      }

      // Small delay to ensure audio context has time to release resources
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    // Create and configure new playback engine
    this.playback = this.#createPlayback(strategy);
    this.playback.setPerformanceProfile(this.performanceProfile);

    if (this._initialized) {
      await this.playback.init({
        Tone,
        effectRacks: this.effectRacks,
        effectOrder: this.axisOrder,
      });
      this._rewireGlobalChain();
      this._playbackStopSubscription = this.playback.addStopListener(this._handlePlaybackStopped);
      if (typeof this.playback.addBufferingListener === 'function') {
        this._playbackBufferingSubscription = this.playback.addBufferingListener(this._handlePlaybackBuffering);
      }
    }

    // The rendering backend changed: rebind hosted source engines (granular)
    // to the new sink's surface. An engine keeps its construction-time mode
    // otherwise — one built beside the streamed voice renders natively, which
    // is inaudible next to the stretch worklet, so granular would stay dead
    // after the full-track unlock for every module, including ones added later.
    this._sourceEngines.rebind();

    // Give the new sink the loop. Nothing was carried across the awaits and nothing can have been
    // lost in them: the loop lives on the adapter, and this just mirrors it onto the fresh backend.
    // An edit that landed mid-swap is simply the loop now, with no snapshot to overwrite it.
    this._resolveLoopEngagement();
  }

  #resolvePerformanceProfile(profile) {
    const fallback = AUDIO_PERFORMANCE_PRESETS.MID;
    if (!profile || typeof profile !== 'object') {
      return {
        ...fallback,
        adaptiveAudioMode: false,
        effectQuality: { ...fallback.effectQuality },
      };
    }
    const key =
      typeof profile.key === 'string' && AUDIO_PERFORMANCE_PRESETS[profile.key.toUpperCase()]
        ? profile.key.toUpperCase()
        : null;
    const base = key ? AUDIO_PERFORMANCE_PRESETS[key] : fallback;
    const adaptiveMode = typeof profile.adaptiveAudioMode === 'boolean'
      ? profile.adaptiveAudioMode
      : false;
    const resolved = {
      ...base,
      ...profile,
      adaptiveAudioMode: adaptiveMode,
      key: key || profile.key || base.key,
      effectQuality: {
        ...base.effectQuality,
        ...(typeof profile.effectQuality === 'object' ? profile.effectQuality : {}),
      },
    };

    // Mobile devices always use compressed assets to reduce memory pressure.
    try {
      if (isMobileDevice()) {
        resolved.assetFormat = 'mp3';
      }
    } catch {}

    return resolved;
  }

  #computeSafeRampSeconds(profile = this.performanceProfile) {
    const fallbackMs = AUDIO_PERFORMANCE_PRESETS.MID.safeRampTimeMs || 45;
    const ms = Number(profile?.safeRampTimeMs);
    const resolvedMs = Number.isFinite(ms) && ms > 0 ? ms : fallbackMs;
    return Math.max(0.001, resolvedMs / 1000);
  }

  /**
   * The requested buffer size is fixed for the life of the context — the browser reads
   * `latencyHint` in the AudioContext constructor and never again. Switching audio
   * quality mid-session therefore changes decode format, effect quality and ramp times,
   * but it cannot change the device callback size; that was decided at boot by
   * `audioContextBoot.js`. Say so once when the two disagree instead of writing to a
   * property that has no setter, which is what made the buffer flag look inert.
   */
  #reportContextLatencyHintMismatch() {
    const requested = this.performanceProfile?.audioContextLatencyHint;
    if (!requested || this._latencyHintMismatchReported) return;

    const context = typeof Tone.getContext === 'function' ? Tone.getContext() : Tone.context;
    const active = context?.latencyHint;
    if (!active || active === requested) return;

    this._latencyHintMismatchReported = true;
    console.info(
      `[audio] this profile asks for a "${requested}" buffer but the context was built "${active}"; ` +
      'the buffer size only changes on reload.',
    );
  }

  setPerformanceProfile(profile) {
    const resolved = this.#resolvePerformanceProfile(profile);
    this.performanceProfile = resolved;
    this.performanceProfileKey = resolved?.key ?? this.performanceProfileKey ?? 'MID';
    this.safeRampSeconds = this.#computeSafeRampSeconds(resolved);
    const desiredStrategy = this.#resolveDecodeStrategy(resolved);
    const currentStrategy = typeof this.playback?.getDecodeStrategy === 'function'
      ? this.playback.getDecodeStrategy()
      : null;
    if (currentStrategy && currentStrategy !== desiredStrategy) {
      if (this.playback?.isPlaying?.() || this.transport?.isRunning) {
        this._pendingDecodeStrategy = desiredStrategy;
      } else {
        this._pendingDecodeStrategy = null;
        void this.#swapPlayback(desiredStrategy);
      }
    }
    this.playback?.setPerformanceProfile(resolved);
    this.#reportContextLatencyHintMismatch();
    // EVERY dimension chain, not just the active one: `_rewireGlobalChain` wires all chains in
    // series into the audio path, so a dimension the user has not selected is still processing.
    // Fanning over `this.effectRacks` alone left the unselected dimensions on the old oversampling
    // and ramp settings — burning exactly the CPU an adaptive downgrade was meant to reclaim.
    this._dimensionChains.forEach((chain) => {
      AXES.forEach((axis) => chain?.axisRacks?.[axis]?.setPerformanceProfile?.(resolved));
    });
  }

  async initialize() {
    this.masterMeter?.dispose?.();
    this.masterMeter = null;

    this.playback.setPerformanceProfile(this.performanceProfile);
    this.#reportContextLatencyHintMismatch();

    // Create audio chain:
    //   normalizationGain → inputLevelGain → [effects] → bodyLevelGain → masterGain → limiter → output
    // Note: normalizationGain / inputLevelGain connect dynamically in _rewireGlobalChain
    this.normalizationGain = new Tone.Gain(1);
    this.inputLevelGain = new Tone.Gain(1);
    this._inputStageTarget = null; // fresh node — no downstream edge to remember yet
    this.bodyLevelGain = new Tone.Gain(1);
    // Final global output trim (deck headroom is mostly at each stage input; this is the last touch).
    this.masterGain = new Tone.Gain(dbToLinear(OUTPUT_HEADROOM_DB));

    enforceStereo(this.normalizationGain);
    enforceStereo(this.inputLevelGain);
    enforceStereo(this.bodyLevelGain);
    enforceStereo(this.masterGain);

    // Connect master chain (normalizationGain connects dynamically)
    this.bodyLevelGain.connect(this.masterGain);

    this.masterMeter = new Tone.Meter({ smoothing: 0.8 });
    enforceStereo(this.masterMeter);

    this._createLevelTaps();

    // Terminal output (decision 0001): single-orbiter → own limiter → speakers; multi-orbiter
    // (an injected shared master bus) → mix in WITHOUT a per-voice limiter (the shared host limits).
    this._connectTerminalOutput();

    // Apply normalization gain from track metadata
    this._applyNormalizationGain();

    const effectsConfig = this.engineConfig?.effects || {};
    const meta = effectsConfig?.__meta || {};
    if (Array.isArray(meta.dimensionOrder)) {
      this._setDimensionOrder(meta.dimensionOrder);
    }
    if (typeof meta.activeDimensionId === 'string' && meta.activeDimensionId.length) {
      this._effectsMeta.activeDimensionId = meta.activeDimensionId;
    }
    const clonedConfig = this._cloneEffectsConfig(effectsConfig);
    const initialDimensionId =
      effectsConfig?.__meta?.activeDimensionId
      ?? clonedConfig.x?.dimensionId
      ?? clonedConfig.y?.dimensionId
      ?? clonedConfig.z?.dimensionId
      ?? DEFAULT_DIMENSION_ID;

    if (shouldPrimeDimensionConfig(clonedConfig)) {
      this._primeDimensionIdsFromConfig(clonedConfig);
    }

    await this.transport.init({ Tone });

    await this.playback.init({ Tone, effectOrder: this.axisOrder });
    if (this._playbackStopSubscription) {
      this._playbackStopSubscription();
    }
    this._playbackStopSubscription = this.playback.addStopListener(this._handlePlaybackStopped);
    if (this._playbackBufferingSubscription) {
      this._playbackBufferingSubscription();
    }
    this._playbackBufferingSubscription =
      typeof this.playback.addBufferingListener === 'function'
        ? this.playback.addBufferingListener(this._handlePlaybackBuffering)
        : null;

    const knownDimensionIds = new Set([
      initialDimensionId,
      ...Array.from(this._dimensionIds || []),
      ...Array.from(this._collectDimensionIdsFromEffectsConfig(clonedConfig)),
    ]);

    const initialDimensionConfig = this._ensureEffectsConfigDimension(clonedConfig, initialDimensionId);
    this.effectsConfigByDimension.set(initialDimensionId, initialDimensionConfig);

    knownDimensionIds.forEach((dimensionId) => {
      if (!dimensionId) return;
      if (dimensionId === initialDimensionId) {
        return;
      }
      const scopedConfig = this._ensureEffectsConfigDimension(clonedConfig, dimensionId);
      this.effectsConfigByDimension.set(dimensionId, scopedConfig);
    });

    this._dimensionOrder.forEach((dimensionId) => {
      if (!dimensionId) return;
      const config = this.effectsConfigByDimension.get(dimensionId);
      this._ensureDimensionChain(dimensionId, config);
    });
    this._rewireGlobalChain();
    this.setActiveDimension(initialDimensionId);
    this._registerParameterSubscriptions();
    // A freshly loaded voice starts at the BEGINNING, stated explicitly.
    //
    // `_startPosition` is the play-from cursor, and leaving it null does not mean "the top" — it
    // means "nobody has chosen", which both the shared-clock catch-up and the own-grid launch snap
    // read as permission to place the playhead themselves. So a track loaded into a synced
    // collection opened wherever the free-running session beat had got to (page-open time wrapped
    // into the track), and a track with a grid marker opened on the marker instead: two different
    // answers, neither of them the beginning, and neither asked for. Nothing else ever sets this —
    // `setStartPositionMs` has no callers — so null was the state of every track a user loaded.
    //
    // Both of those layers already yield to an EXPLICIT start position (`_startPositionIsExplicit`),
    // so saying "zero" here is all it takes for "the user has not chosen" to mean "the start". The
    // cursor is consumed by the first start, after which pausing and resuming behaves as before.
    //
    // NOT in a room, where the catch-up is the whole point and this would be actively destructive:
    // an explicit cursor makes `_settleStartPosition` call `_anchorPhaseAtCurrentPosition`, which
    // REDEFINES this voice's shared phase to "at the beat now, I am at zero". A voice loading into a
    // live session would not merely fail to take the room's position — it would rewrite the phase
    // every peer projects this voice from, and drag them all to its own start.
    //
    // Read from `_multiplayer` — the boot-frozen answer this adapter already resolved — not from the
    // room store: `initRoomClient` runs inside an async identity handshake, so live room state is
    // still false while a voice builds its engine and would answer "not a room" for the whole of
    // that voice's life. That is the race `isMultiplayerBoot` exists for, and the field also honours
    // an explicit `multiplayer` passed to the constructor.
    // `defaulted` marks it as OURS, not a choice the user made. The launch grid reads that
    // difference: it yields the bar wait and the count-in to a placed playhead ("start here, now"),
    // which a default must not claim to be — see `_startPositionWasPlacedByUser`.
    if (!this._multiplayer) this._startPosition = { rawMs: 0, landedMs: 0, defaulted: true };
    this._updatePlaybackState('stopped', { force: true, source: 'initialize' });
    this.mediaSession?.init?.();
    this._initialized = true;
  }

  /**
   * Wire the voice's terminal output (decision 0001).
   * - Single-orbiter (no injected `_outputNode`): this adapter owns the path to the speakers, so it
   *   builds its own limiter (-1 dB ceiling) → meter + `Tone.Destination`. Byte-identical to before.
   * - Multi-orbiter (an `outputNode` = the shared master bus was injected): the voice mixes straight
   *   into the shared bus and builds NO limiter — the `MultiOrbiterAudioHost` owns the single limiter
   *   on the summed mix (avoid N limiters). The meter taps `masterGain` (pre-sum) for per-voice level.
   * `this.limiter` stays null in that case; every consumer of it is null-guarded (`?.`).
   */
  _connectTerminalOutput() {
    if (this._outputNode) {
      this.limiter = null;
      this.masterGain.connect(this.masterMeter);
      this.masterGain.connect(this._outputNode);
      return;
    }
    this.limiter = new Tone.Limiter(-1); // Ceiling at -1 dB to prevent clipping
    enforceStereo(this.limiter);
    this.masterGain.connect(this.limiter);
    this.limiter.connect(this.masterMeter);
    // `getDestination()`, never the `Tone.Destination` export: that one is captured when the
    // Tone module is evaluated and stays bound to whatever context existed then, which is not
    // the context the app runs on. Connecting to it throws across contexts.
    this.limiter.connect(Tone.getDestination());
  }

  async preload() {
    await this.playback.load();
    // Duration is only knowable once the audio is loaded, and the saved loop has to be clamped
    // against it — so the restore happens here rather than at construction.
    this.restoreSavedLoop();
  }

  /**
   * Re-apply this user's saved loop for this track: the region they drew and whether it
   * was on. Resolved by the shared track-metadata helpers, so it follows the same ladder as tempo
   * and warp — this viewer's own saved loop, else the owner's, else nothing.
   *
   * Three things this deliberately does NOT do:
   *  - it never MANUFACTURES a loop. A saved "on" with no saved region is inert and is left alone;
   *    inventing a full-track loop here is the exact behaviour that was removed from `play()`,
   *    because a loop nobody drew silently reappearing reads as a bug, not a preference.
   *  - it never writes as the user. `system: true` means the room's ownership gate does not apply
   *    and nothing is published — restoring your own saved loop must not retune everyone else's.
   *  - it never trusts the stored numbers. A region saved against an earlier, longer version of the
   *    audio would otherwise point past the end, so it is clamped through the one canonical helper.
   */
  restoreSavedLoop() {
    // Not in a room. There the loop is SHARED state owned by whoever holds the deck, and the joiner
    // adopts it from the room snapshot — a personal saved loop applied on top would leave this client
    // looping a region nobody else is playing. Worse, the restore lands at preload(), which can be
    // after the snapshot has already converged, so it would silently undo that convergence.
    if (this._multiplayer) return;

    const range = resolveLoopRangeSecFromTrackData(this.trackData);
    if (!range) return;

    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    // `normalizeLoopRange` clamps the END against the duration but not the START — for a live edit
    // the start is always already inside the audio. A RESTORED region carries no such guarantee: a
    // loop drawn at 4:00 on a five-minute track, re-released at two minutes, would come back starting
    // past the end, and the helper would happily return a range the playhead can never reach. So the
    // start is bounded here first, and the whole thing dropped if there is no room left for a loop.
    if (durationMs > 0 && range.startSec * 1000 >= durationMs) return;
    const clamped = normalizeLoopRange(range.startSec * 1000, range.endSec * 1000, {
      durationMs: durationMs > 0 ? durationMs : null,
    });
    if (!clamped) return;

    // A saved loop with no saved on/off predates nothing and means nothing on its own — treat the
    // absence as "armed but off" so the region comes back visible without starting to loop unasked.
    const enabled = resolveLoopEnabledFromTrackData(this.trackData) === true;
    this.setLoopRange(clamped.start, clamped.end, { active: enabled, system: true });
  }

  /** Grid 'none' still needs SOME boundary every client can name for a shared event — one quarter
   *  note, the smallest wait that survives the network, rather than a bar the user never selected.
   *  The floor for every shared quantum: start, seek, and a scheduled correction alike. */
  static SHARED_MIN_QUANTUM_BEATS = 1;

  async play({ quantize = 'auto', atSharedBeat = null, roomRemote = false } = {}) {
    // Room mode (bridge installed): transport is a SHARED event. Ownership is checked FIRST — a
    // press on a voice this player doesn't hold must touch nothing. The publish itself happens
    // further down, once this client is actually ready to schedule.
    if (this._roomTransport && !roomRemote && this._roomTransport.canControl?.() === false) return;

    await ensureSilentAudioUnlock();

    // A play during an in-flight "Unlock speed" reload JOINS it (the reload owns the backend and
    // the one download — a parallel load() here would start a second fetch/decode of the same asset).
    if (this._bufferedReloadPromise) {
      try { await this._bufferedReloadPromise; } catch (_) {}
    }

    // A fresh play supersedes any pending quantized start or room-scheduled stop.
    this._cancelPendingQuantizedStart();
    this._cancelPendingRoomStop();

    const canApplyPendingStrategy = !this.playback?.isPlaying?.() && !this.transport?.isRunning;
    if (this._pendingDecodeStrategy && canApplyPendingStrategy) {
      try {
        await this.#swapPlayback(this._pendingDecodeStrategy);
        const currentStrategy = this.playback?.getDecodeStrategy?.();
        if (currentStrategy === this._pendingDecodeStrategy) {
          this._pendingDecodeStrategy = null;
        }
      } catch (error) {
        console.error('[AudioEngineAdapter] Failed to apply pending decode strategy:', error);
        // Keep pending so we can retry later
      }
    }
    if (!this.playback.isLoaded) {
      await this.playback.load();
    }
    // Play settles the loop like every other path: a loop the user left ON engages if the playhead
    // is inside it, and waits (visibly held) if it is not. There is no automatic full-track loop —
    // creating one on play made "loop the whole song" the hidden initial state of every voice, which
    // is why a loop could silently reappear or grow back after a pause. A loop is only ever drawn.
    this._resolveLoopEngagement();
    if (this.playback.isPlaying()) {
      return;
    }

    await this._applyPendingStartPosition();

    // Room mode sender: pick the shared target beat and publish it only NOW — after unlock and
    // load — so the beat is chosen when this client can actually schedule against it (publishing
    // before the load would let the peers fire on a beat the sender then misses). A remote event
    // carries its beat in `atSharedBeat`; atBeat null = "start now" everywhere (no joined clock).
    let targetSharedBeat = Number.isFinite(atSharedBeat) ? atSharedBeat : null;
    if (this._roomTransport && !roomRemote) {
      targetSharedBeat = this._nextSharedBarBeat();
      this._roomTransport.publishStart?.(targetSharedBeat);
    }

    // Flag is absolute: 'bar' and 'auto' both require _shouldQuantizeStart().
    // 'off' disables even when the flag is on.
    // A room-shared target beat always schedules (every client must land on the same beat).
    const shouldQuantize = targetSharedBeat !== null
      || (quantize !== 'off' && this._shouldQuantizeStart());
    if (!shouldQuantize) {
      await this._settleStartPosition();
      // Own-grid launch snap: with NO joined shared clock, a deck with warp on comes in on its OWN
      // bar — the align above no-ops without a joined clock, and this snaps the paused position to
      // the nearest launch-grid boundary of the deck's own grid instead. Mutually exclusive gates.
      if (quantize !== 'off') await this._snapStartPositionToOwnGrid();
      await this._startTransportAndPlayback();
      this._updatePlaybackState('playing', { source: 'play' });
      return;
    }

    const delayMs = targetSharedBeat !== null
      ? this._delayMsToSharedBeat(targetSharedBeat)
      : this._computeBarDelayMs();
    if (!Number.isFinite(delayMs) || delayMs <= 0) {
      await this._settleStartPosition({ atSharedBeat: targetSharedBeat });
      await this._startTransportAndPlayback();
      this._updatePlaybackState('playing', { source: 'play' });
      return;
    }

    if (isDebugSyncLoggingEnabled()) {
      try {
        console.debug('[quantize] scheduling bar-quantized start', {
          delayMs,
          bpm: syncCoordinator?.bpm,
          nowBeat: syncCoordinator?.getCurrentBeat?.(),
        });
      } catch (_) {}
    }

    // The SHARED beat this start is scheduled on travels with the pending slot: when the start honours
    // a chosen position it anchors the phase at THAT beat, which every client agrees on, rather than at
    // whatever beat its own timer happened to fire on.
    const pending = { timer: null, canceled: false, atSharedBeat: targetSharedBeat };
    this._pendingQuantizedStart = pending;

    // Surface the count-in so the UI shows the wait is ARMED (not broken). Emitted with the
    // target fire time + tempo/grid so the Transport can render a beat countdown; cleared on
    // fire (_firePendingQuantizedStart) and on cancel (_cancelPendingQuantizedStart).
    this._emitCountIn(delayMs);

    pending.timer = setTimeout(() => {
      pending.timer = null;
      this._firePendingQuantizedStart(pending);
    }, delayMs);
  }

  /**
   * Publish the quantized-start count-in (the launch-grid wait). The target fire time +
   * tempo let the UI render a beat countdown that matches the scheduled start. Tempo comes from the
   * SAME shared-clock/SyncCoordinator source the bar-delay used (read synchronously right after, so
   * it can't drift). No-op without a valid tempo or a `performance` clock the UI can share.
   * @param {number} delayMs the scheduled delay before the start fires.
   */
  _emitCountIn(delayMs, { seekTargetSec } = {}) {
    if (!Number.isFinite(delayMs) || delayMs <= 0) return;
    // The UI computes `targetTime - performance.now()`, so the cue needs that same monotonic clock.
    if (typeof performance === 'undefined') return;
    // Tempo for the countdown: the shared clock (synced), else this deck's OWN running clock (an
    // unsynced own-grid wait counts at the tempo actually playing — varispeed included), else the
    // coordinator master (the legacy forced-quantize path).
    const shared = this._sharedClockBeat();
    const own = shared ? null : (this.deck.clock?.() ?? null);
    const bpm = Number(shared ? shared.tempoBpm : (own ? 60 / own.secondsPerBeat : syncCoordinator?.bpm));
    if (!Number.isFinite(bpm) || bpm <= 0) return;
    const state = { active: true, targetTime: performance.now() + delayMs, bpm };
    // Piece 6: a quantized SEEK carries its target position so the waveform can blink a marker there.
    if (Number.isFinite(seekTargetSec)) state.seekTargetSec = seekTargetSec;
    this._setCountInState(state);
  }

  /** Clear the count-in (the launch bar fired, or the pending start was canceled). */
  _clearCountIn() {
    if (!this._countInState?.active) return;
    this._setCountInState({ active: false });
  }

  /** Single writer of the count-in snapshot + its `orbiters:quantize-countin` mirror event.
   *  Dispatched on this voice's eventBus (window for single-orbiter → byte-identical). */
  _setCountInState(state) {
    this._countInState = state;
    if (this._eventBus && typeof this._eventBus.dispatchEvent === 'function') {
      this._eventBus.dispatchEvent(new CustomEvent('orbiters:quantize-countin', { detail: state }));
    }
  }

  /** The current count-in snapshot (read by the React Transport surface). */
  getCountInState() {
    return this._countInState;
  }

  async _firePendingQuantizedStart(pending) {
    if (!pending || pending.canceled) return;
    // The launch bar arrived — the count-in is over; playback takes over below.
    this._clearCountIn();
    try {
      await this._settleStartPosition({ atSharedBeat: pending.atSharedBeat ?? null });
      if (pending.canceled) return;
      await this._startTransportAndPlayback();
      // Race: cancel may have fired during transport.start / triggerPlay awaits.
      // If so, playback has actually begun — tear it down before returning.
      if (pending.canceled) {
        await this._abortStartedPlayback();
        this._updatePlaybackState('stopped', { source: 'play-quantized-canceled' });
        return;
      }
      this._updatePlaybackState('playing', { source: 'play-quantized' });
    } catch (err) {
      if (isDebugSyncLoggingEnabled()) {
        try {
          console.warn('[quantize] delayed play rejected, retrying immediately', err);
        } catch (_) {}
      }
      if (pending.canceled) return;
      try {
        await this._startTransportAndPlayback();
        if (pending.canceled) {
          await this._abortStartedPlayback();
          this._updatePlaybackState('stopped', { source: 'play-quantized-canceled' });
          return;
        }
        this._updatePlaybackState('playing', { source: 'play-quantized-fallback' });
      } catch (err2) {
        // Transport was stopped inside the helper; force UI to a stable stopped
        // state so we don't leave it stuck in whatever prior state.
        try { console.warn('[quantize] fallback start also failed', err2); } catch (_) {}
        this._updatePlaybackState('stopped', { force: true, source: 'play-quantized-error' });
      }
    } finally {
      if (this._pendingQuantizedStart === pending) {
        this._pendingQuantizedStart = null;
      }
    }
  }

  async _startTransportAndPlayback() {
    // Preserve existing order: transport first, then playback. If playback
    // fails, stop the transport so we don't leave it running without audio.
    await this.transport.start();
    try {
      await this.playback.triggerPlay();
    } catch (error) {
      try { await this.transport.stop(); } catch (_) {}
      throw error;
    }
    // Consumed only once playback is really running: a start that threw leaves the chosen position
    // standing, so pressing Play again still begins where the user asked. A later play, from wherever
    // playback left off, is an ordinary launch and belongs back on the grid.
    this._startPosition = null;
  }

  async _abortStartedPlayback() {
    // Tear down a just-started transport+playback pair after a cancel race.
    // Stop playback first so it doesn't keep advancing while transport winds
    // down, then stop transport.
    try { await this.playback?.triggerStop?.(); } catch (_) {}
    try { await this.transport?.stop?.(); } catch (_) {}
  }

  /**
   * The read-only shared-clock snapshot, iff a shared session is joined.
   * Returns `{ beatNow, tempoBpm, quantum, … }` from the validated `BeatTimeline` (single-device or
   * network), else null. The audio path NEVER imports BeatTimeline — it only reads this snapshot.
   * @returns {{ joined: true, beatNow: number, phaseNow: number, tempoBpm: number, quantum: number } | null}
   */
  _sharedClockBeat() {
    // Gate on THIS deck's own sync-enabled state first. The shared clock is REALM-WIDE — "joined" as
    // soon as ANY two voices in the collection are synced — so without this check, a voice with its
    // own sync explicitly OFF would still quantize its play/seek against siblings' sessions
    // (bar-delay + count-in), purely because other voices opted in.
    if (this.deck.getStatusDetail()?.enabled !== true) return null;
    // Read the shared-clock snapshot through this deck (its source points at the realm-wide shared
    // clock today; later a per-voice or remote clock, with no adapter change). The joined filter
    // stays here — the deck's source is a pass-through.
    const state = this.deck.getSharedClockState();
    return state && state.joined === true ? state : null;
  }

  /** Own-grid launch snap: with warp on and a launch grid set, Play starts ON this deck's own grid
   *  — the paused position moves to the NEAREST launch-grid boundary (grid marker + native tempo).
   *  Gated on the JOINED SHARED CLOCK, not on the sync flag: a deck with sync on but no joined
   *  session has no shared beat to align to, so it belongs on its own grid like any other lone deck.
   *  The deck owns the snap math (`snapToOwnGridMs`); 'none' on the launch grid = no snap. */
  async _snapStartPositionToOwnGrid() {
    if (this._sharedClockBeat() !== null) return; // a joined shared clock aligns instead
    if (this._startPositionIsExplicit()) return; // the user picked this spot — start ON it
    const currentMs = Number(this.playback?.getCurrentPositionMs?.() ?? 0);
    // The track start is a launch point in its own right. The grid marker is the DETECTED downbeat,
    // sitting at whatever offset into the audio the analysis put it — so snapping a playhead that is
    // still at zero moves Play to a spot that looks random from the outside and quietly drops the
    // opening of the track. A playhead nobody has moved is at the beginning, and the beginning is
    // where Play belongs. Every position the user actually moved to still snaps: resuming from a
    // pause mid-track is an ordinary launch and does come in on the deck's own bar.
    if (currentMs < 1) return;
    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    const targetMs = this.deck.snapToOwnGridMs(currentMs, {
      launchGridQuarterBeats: this.deck.launchGridQuarterBeats,
      durationMs,
      loopRange: this._loopRangeAt(currentMs),
    });
    if (!Number.isFinite(targetMs) || Math.abs(targetMs - currentMs) < 1) return;
    await this.playback?.setPosition?.(targetMs);
    this._resolveLoopEngagement(targetMs); // the playhead moved — the loop is re-decided from where it landed
    this.mediaSession?.syncPositionState?.();
  }

  _shouldQuantizeStart() {
    // Launch quantize follows WARP, not sync: a warp-off player starts immediately — it plays at
    // its natural speed, so making it wait for a bar line it won't track is a stumble, not a snap.
    // And the SHARED bar-delay belongs only to a deck that is itself synced: an unsynced deck's
    // launches ride its OWN grid (the position-snap path), never the session's bar.
    if (this.deck?.following !== true) return false;
    const synced = this.deck?.isCollection === true
      ? this.deck?.syncEnabled === true
      : syncCoordinator?.isEnabled === true;
    if (!synced) return false;
    // A joined shared clock self-enables quantize — it already proves a shared session + a valid
    // tempo.
    if (this._sharedClockBeat() !== null) return true;
    // ALONE, and that is fine: the launch grid is a property of THIS player's transport, not of
    // having company. A synced deck with a running tempo waits for its own next bar and counts in,
    // exactly as it does in a session — otherwise a grid reading "1 Bar" would quietly do nothing
    // until someone else showed up. The bar delay falls back to the coordinator's beat (see
    // `_computeBarDelayMs`), which is this player's own continuously running transport clock.
    if (syncCoordinator?.isEnabled !== true) return false;
    const bpm = Number(syncCoordinator?.bpm);
    if (!Number.isFinite(bpm) || bpm <= 0) return false;
    // …unless the user placed the playhead for this start. Then "start" means start, here and now:
    // counting out a bar first (and snapping the spot) is the opposite of what they asked for. Only
    // reachable with NO joined shared clock — a room start returns above and stays shared.
    //
    // PLACED, not merely explicit: a freshly loaded voice carries a DEFAULTED cursor at zero so that
    // nothing silently moves its start position, and reading that as "the user asked to start right
    // here" would drop the bar wait and the count-in from the first Play of every track loaded.
    if (this._startPositionWasPlacedByUser()) return false;
    return true;
  }

  /**
   * Did the USER put the playhead where it is for this start? The launch grid yields only to a real
   * choice — a cursor this engine defaulted at load says where playback begins, but it is nobody
   * asking to start on that spot this instant, so it must not disarm the bar wait.
   */
  _startPositionWasPlacedByUser() {
    return this._startPositionIsExplicit() && this._startPosition?.defaulted !== true;
  }

  /**
   * Is the playhead sitting on a position the user placed for this start? Confirmed against the live
   * playhead rather than trusted on its own, so a stale mark left by an earlier seek cannot quietly
   * disable the launch grid for a start that has nothing to do with it.
   */
  _startPositionIsExplicit() {
    const landedMs = this._startPosition?.landedMs;
    if (!Number.isFinite(landedMs)) return false;
    const positionMs = Number(this.playback?.getCurrentPositionMs?.() ?? NaN);
    return Number.isFinite(positionMs) && Math.abs(positionMs - landedMs) < 2;
  }

  /**
   * Where the next start begins — the play-from cursor. Stopped, the playhead goes there now (so the
   * marker and the playhead agree, and the start honours it). PLAYING, it is remembered and applied
   * when playback next starts: the alternative — seeking once playback has already opened — begins
   * the sound at the old spot and then jumps, which is exactly the "it started in the wrong place"
   * this fixes. An explicit seek in the meantime supersedes it (see `_seekNowBody`).
   */
  /**
   * Put the playhead on the start position the user chose, if it is not there already. Called at the
   * top of the start path — BEFORE anything aligns, snaps or opens the audio — so the sound begins at
   * that spot. Applying it after the start instead opened playback at the old position and jumped
   * afterwards, which is what "it started from the wrong place" was. Through the public seek, so a
   * room hears it too.
   */
  async _applyPendingStartPosition() {
    const target = this._startPosition?.rawMs;
    if (!Number.isFinite(target)) return;
    if (this._startPositionIsExplicit()) return; // the playhead is already on it
    // In a room the start position is shared like any other seek, so it is PUBLISHED — with the null
    // beat ("now, everywhere"), because the start this precedes is what carries the shared timing.
    if (this._roomTransport && this._roomTransport.canControl?.() !== false) {
      this._roomTransport.publishSeek?.(null, target);
    }
    // Landed locally with an AWAIT, never merely scheduled: the start is moments away, and a seek
    // still in flight when it fires is exactly the "playback opened in the wrong place" this fixes.
    // (Going through `seekToMilliseconds` would only schedule it in a room and return.)
    await this._seekNow(target, { placed: true });
  }

  setStartPositionMs(ms) {
    const target = Number(ms);
    if (!Number.isFinite(target)) return;
    // `landedMs` is unknown until it actually lands (the seek below, or the next start) — until then
    // nothing may treat the playhead as being on the spot.
    this._startPosition = { rawMs: Math.max(0, target), landedMs: null };
    // Returns the move so a caller CAN wait for it; the UI does not need to. Stopped, it lands through
    // the same path the start uses — awaited locally and published to the room — so the playhead and
    // the marker agree everywhere, rather than the seek merely being scheduled for a bar that a
    // stopped deck will never reach.
    if (this.playback?.isPlaying?.() !== true) return this._applyPendingStartPosition();
    return Promise.resolve();
  }

  _computeBarDelayMs() {
    const QUANTIZE_SLACK_MS = 40;
    // Prefer the precise, server/device-aligned shared clock when it's driving; fall back to the
    // SyncCoordinator's loose frame-sampled epoch otherwise. Same bar-delay math either way.
    const shared = this._sharedClockBeat();
    // The selectable launch grid is stored in bars. Bars follow THIS voice's OWN meter (a 6/8 voice
    // snaps to a 6/8 bar, a 4/4 voice to a 4/4 bar) over the shared beat clock — the shared clock is
    // meter-agnostic and supplies only the beat + tempo (beatNow / tempoBpm) below.
    const beatsPerBar = this.deck.launchGridQuarterBeats;
    // Launch grid = none (0) → no snap: fire immediately (delay 0 → play() takes the un-quantized path).
    if (!(beatsPerBar > 0)) return 0;
    const bpm = Number(shared ? shared.tempoBpm : syncCoordinator?.bpm);
    const nowBeat = Number(shared ? shared.beatNow : syncCoordinator?.getCurrentBeat?.());
    if (!Number.isFinite(bpm) || bpm <= 0) return 0;
    if (!Number.isFinite(nowBeat)) return 0;
    const secondsPerBeat = 60 / bpm;
    const msPerBar = beatsPerBar * secondsPerBeat * 1000;
    const EPS = 1e-6;
    const nextBarBeat = Math.ceil((nowBeat + EPS) / beatsPerBar) * beatsPerBar;
    let delayMs = (nextBarBeat - nowBeat) * secondsPerBeat * 1000;
    if (delayMs < QUANTIZE_SLACK_MS) delayMs += msPerBar;
    return delayMs;
  }

  /**
   * Install (or clear, with null) the room transport bridge. When present, play()/stop() become
   * SHARED events: a local press publishes "do X at shared beat N" through the bridge and this
   * adapter schedules itself against the same beat; the bridge's `canControl` gates whose input may
   * drive this voice at all. Room mode only — null keeps every path byte-identical.
   * @param {{ canControl?: () => boolean,
   *           publishStart?: (atBeat: number|null) => void,
   *           publishStop?: (atBeat: number|null) => void }|null} bridge
   */
  setRoomTransportBridge(bridge) {
    this._roomTransport = bridge && typeof bridge === 'object' ? bridge : null;
    if (!this._roomTransport) this._cancelPendingRoomStop();
  }

  /**
   * Would a transport press do anything right now? True outside a room; in one, true for a deck
   * this player holds AND that is past its load barrier.
   *
   * The barrier is deliberately part of the answer rather than a separate question. It is not a
   * permission — a deck still arming is one every holder is waiting on, not one that is somebody
   * else's — but the point of this accessor is what the button in front of the person may claim,
   * and a press during that window starts nothing either way. Ownership is the broader gate the
   * input layer asks (`roomStore.canSelfDrive`), so a shortcut aimed at a held-but-arming deck
   * passes there and stops here, with no false repaint; the room already draws those decks as
   * loading (`roomStore.armingDecks`), which is where that state is explained.
   *
   * `play`/`stop`/`seek` already refuse a press they may not act on — silently, because there is
   * nothing sensible to throw at a shared session and nothing to await. That silence is right for
   * the audio and wrong for the button in front of the person: the transport control paints the new
   * state BEFORE it asks the engine (so a press feels immediate), and a refusal it cannot see leaves
   * it showing "playing" over a deck that never started. So the same answer is readable from the
   * outside — one accessor onto the gate that already decides, not a second copy of the rule.
   */
  canControlTransport() {
    return !this._roomTransport || this._roomTransport.canControl?.() !== false;
  }

  /**
   * Install (or clear, with null) the room LOOP bridge. When present, a USER loop edit (enable/range/
   * clear from the waveform panel or its MIDI) becomes a SHARED event: the local edit is applied AND
   * published through the bridge, and `canControl` gates whose input may drive this voice's loop.
   * A remote edit (or an internal/system loop write) passes `{ roomRemote:true }` / `{ system:true }`
   * to apply WITHOUT re-publishing or gating. Room mode only — null keeps every path byte-identical.
   * @param {{ canControl?: () => boolean,
   *           publishRange?: (startMs: number, endMs: number, active: boolean) => void,
   *           publishEnable?: (active: boolean) => void,
   *           publishClear?: () => void }|null} bridge
   */
  setRoomLoopBridge(bridge) {
    this._roomLoop = bridge && typeof bridge === 'object' ? bridge : null;
  }

  /** True when a USER loop edit must be blocked (a room bridge is installed and won't grant control). */
  _roomLoopBlocked(roomRemote, system) {
    return Boolean(this._roomLoop) && !roomRemote && !system && this._roomLoop.canControl?.() === false;
  }

  /**
   * The next shared-grid boundary as an ABSOLUTE beat, or null when no joined shared clock is
   * available (callers then fall back to "now" everywhere). Same slack rule as `_computeBarDelayMs`:
   * a boundary closer than the scheduling headroom rolls to the next one.
   *
   * The quantum is THIS DECK'S OWN LAUNCH GRID — the grid means the same thing in a room as it does
   * alone, for starts, seeks and corrections alike. Grid 'none' is the only exception: a shared
   * event still needs SOME boundary every client can name, so it falls to the smallest honest one
   * (`SHARED_MIN_QUANTUM_BEATS`) rather than a bar the user never asked for.
   */
  _nextSharedBarBeat() {
    const shared = this._sharedClockBeat();
    if (!shared) return null;
    const beatsPerBar = this.deck.launchGridQuarterBeats > 0
      ? this.deck.launchGridQuarterBeats
      : AudioEngineAdapter.SHARED_MIN_QUANTUM_BEATS;
    const bpm = Number(shared.tempoBpm);
    const beatNow = Number(shared.beatNow);
    if (!Number.isFinite(bpm) || bpm <= 0 || !Number.isFinite(beatNow)) return null;
    const QUANTIZE_SLACK_MS = 40;
    const EPS = 1e-6;
    const msPerBeat = 60_000 / bpm;
    let target = Math.ceil((beatNow + EPS) / beatsPerBar) * beatsPerBar;
    if ((target - beatNow) * msPerBeat < QUANTIZE_SLACK_MS) target += beatsPerBar;
    return target;
  }

  /** The next shared-grid whole BEAT (the stop quantum) as an absolute beat, or null. */
  _nextSharedBeat() {
    const shared = this._sharedClockBeat();
    if (!shared) return null;
    const bpm = Number(shared.tempoBpm);
    const beatNow = Number(shared.beatNow);
    if (!Number.isFinite(bpm) || bpm <= 0 || !Number.isFinite(beatNow)) return null;
    const QUANTIZE_SLACK_MS = 40;
    const EPS = 1e-6;
    let target = Math.ceil(beatNow + EPS);
    if ((target - beatNow) * (60_000 / bpm) < QUANTIZE_SLACK_MS) target += 1;
    return target;
  }

  /**
   * Wall-clock ms from now to an absolute shared beat (≤0 when the beat already passed — the
   * caller then fires immediately, which is the honest catch-up for a late-delivered event).
   * Null when the shared clock is gone; the caller treats that as "now" too.
   */
  _delayMsToSharedBeat(targetBeat) {
    const shared = this._sharedClockBeat();
    if (!shared || !Number.isFinite(targetBeat)) return null;
    const bpm = Number(shared.tempoBpm);
    const beatNow = Number(shared.beatNow);
    if (!Number.isFinite(bpm) || bpm <= 0 || !Number.isFinite(beatNow)) return null;
    return (targetBeat - beatNow) * (60_000 / bpm);
  }

  /** Arm a room-shared stop at an absolute shared beat (all clients stop together). */
  _scheduleRoomStopAtBeat(atBeat) {
    this._cancelPendingRoomStop();
    const delayMs = this._delayMsToSharedBeat(atBeat);
    if (!Number.isFinite(delayMs) || delayMs <= 0) {
      return this.stop({ roomRemote: true });
    }
    const pending = { timer: null, canceled: false };
    this._pendingRoomStop = pending;
    pending.timer = setTimeout(() => {
      pending.timer = null;
      if (pending.canceled) return;
      if (this._pendingRoomStop === pending) this._pendingRoomStop = null;
      void this.stop({ roomRemote: true });
    }, delayMs);
    return undefined;
  }

  _cancelPendingRoomStop() {
    const pending = this._pendingRoomStop;
    if (!pending) return;
    pending.canceled = true;
    if (pending.timer) {
      try { clearTimeout(pending.timer); } catch (_) {}
      pending.timer = null;
    }
    this._pendingRoomStop = null;
  }

  _cancelPendingQuantizedStart() {
    const pending = this._pendingQuantizedStart;
    if (!pending) return;
    pending.canceled = true;
    if (pending.timer) {
      try { clearTimeout(pending.timer); } catch (_) {}
      pending.timer = null;
    }
    this._pendingQuantizedStart = null;
    // The armed start was abandoned (pause / superseding play) — drop the count-in so the UI doesn't
    // keep counting toward a bar that will never fire.
    this._clearCountIn();
  }

  async pause() {
    // Cancel before the "not playing" early-return: a pending quantized start
    // is logically not-yet-playing but must still be canceled on pause.
    this._cancelPendingQuantizedStart();
    this._cancelPendingQuantizedSeek(); // an armed seek must not fire after we pause
    this._cancelPendingRoomStop(); // a pause supersedes a beat-scheduled room stop
    this._cancelPendingDriftRealign(); // …and a correction armed for a bar we will not reach

    if (!this.playback.isPlaying() && !this.transport.isRunning) {
      return;
    }
    await this.transport.pause();
    await this.playback.pause();
    this._updatePlaybackState('paused', { source: 'pause' });
  }

  async stop({ atSharedBeat = null, roomRemote = false } = {}) {
    // Room mode: stop is a SHARED event on the beat quantum. A local press publishes "stop at
    // beat N" and schedules itself at that same beat; a remote event arrives with the target and
    // skips the publish. Either way an armed start is disarmed IMMEDIATELY (it must not fire while
    // a stop is on its way), and with no shared clock the stop is immediate everywhere.
    if (this._roomTransport && !roomRemote) {
      if (this._roomTransport.canControl?.() === false) return;
      const hadPendingStart = Boolean(this._pendingQuantizedStart);
      this._cancelPendingQuantizedStart();
      const atBeat = this._nextSharedBeat();
      this._roomTransport.publishStop?.(atBeat);
      if (!hadPendingStart && atBeat !== null && this.playback.isPlaying()) {
        return this._scheduleRoomStopAtBeat(atBeat);
      }
    } else if (roomRemote && Number.isFinite(atSharedBeat)) {
      this._cancelPendingQuantizedStart();
      if (this.playback.isPlaying()) {
        return this._scheduleRoomStopAtBeat(atSharedBeat);
      }
    }

    this._cancelPendingQuantizedStart();
    this._cancelPendingQuantizedSeek();
    this._cancelPendingRoomStop();
    this._cancelPendingDriftRealign();
    if (!this.playback.isPlaying() && !this.transport.isRunning && this._playbackState === 'stopped') {
      return;
    }
    const stopPromise = this.playback.triggerStop();
    await this.transport.stop();
    await stopPromise;
    if (this._pendingDecodeStrategy) {
      try {
        await this.#swapPlayback(this._pendingDecodeStrategy);
        const currentStrategy = this.playback?.getDecodeStrategy?.();
        if (currentStrategy === this._pendingDecodeStrategy) {
          this._pendingDecodeStrategy = null;
        }
      } catch (error) {
        console.error('[AudioEngineAdapter] Failed to apply pending decode strategy:', error);
        // Keep pending so we can retry later
      }
    }
    this._updatePlaybackState('stopped', { source: 'stop' });
  }

  async resumeAfterInterruption() {
    const shouldResume =
      this._playbackState === 'playing' ||
      this.playback?.isPlaying?.() ||
      this.transport?.isRunning;

    if (!shouldResume) {
      return false;
    }

    try {
      await ensureSilentAudioUnlock();
      await Tone.start();
    } catch (_) {}

    if (typeof this.playback?.resumeAfterInterruption === 'function') {
      const resumed = await this.playback.resumeAfterInterruption();
      if (resumed) {
        this._updatePlaybackState('playing', { force: true, source: 'resume-after-interruption' });
        this.mediaSession?.syncPositionState?.();
        // Whatever the interruption cost us in playhead is a phase error the moment we are audible
        // again — converge NOW rather than wait out the sampler's window and cooldown.
        void this.realignToSharedPhase();
        return true;
      }
    }

    return false;
  }

  /**
   * A seek lands IN PHASE. When a shared session is live AND we're playing, defer the
   * seek to the next bar boundary (symmetric to the quantized launch) so it re-anchors on the grid
   * instead of jumping off-phase and losing sync. Solo / not-playing → immediate (byte-identical).
   *
   * In a ROOM a seek is a SHARED event: the slot holder publishes the phase ANCHOR it is
   * establishing ("at shared beat B this voice is at source S") and schedules itself against that
   * same beat; every other client applies the same anchor. A non-holder's seek is dropped by the
   * same ownership gate transport uses. Internal restores (engine swap, buffered reload) pass
   * `system` — they put THIS client's playhead back where it already was and must never reach the
   * room, nor be gated by an ownership rule that has nothing to say about them.
   * @param {number} ms raw source target.
   * @param {{ roomRemote?: boolean, atSharedBeat?: number|null, system?: boolean }} [options]
   */
  async seekToMilliseconds(ms, { roomRemote = false, atSharedBeat = null, system = false } = {}) {
    // A remote seek ALWAYS applies — it is the room's decision, already made. A null beat is its
    // "now" convention (an unsynced deck has no boundary to wait on), not a reason to fall through
    // to the local path and quantize against a grid the sender never used.
    if (roomRemote) {
      // Awaited so the room's per-voice queue stays serialized THROUGH the landing, not just up to
      // the scheduling of it.
      await this._scheduleAnchoredSeek(Number.isFinite(atSharedBeat) ? atSharedBeat : null, ms);
      return;
    }
    if (this._roomTransport && !roomRemote && !system) {
      if (this._roomTransport.canControl?.() === false) return;
      // The quantum is THIS DECK'S launch grid — the grid means the same thing online as alone.
      // A NULL beat is the "now, everywhere" convention transport start/stop already use, and it is
      // the honest answer for a deck the user took OFF the shared pulse (SYNC off): nothing is
      // beat-locked, so there is no boundary to wait for. Either way the seek is PUBLISHED, never
      // refused — every client renders this deck, so a move that doesn't replicate is two people
      // hearing different music.
      const anchorBeat = this._nextSharedBarBeat();
      this._roomTransport.publishSeek?.(anchorBeat, ms);
      await this._scheduleAnchoredSeek(anchorBeat, ms);
      return;
    }
    this._cancelPendingQuantizedSeek(); // a superseding seek replaces a pending one
    this._cancelPendingDriftRealign(); // …and supersedes a correction armed against the old phase
    // A system restore is not a musical seek: it puts the playhead back where it already was, so it
    // must not wait for a bar (nor pick up the anchor/lead the quantized path applies at fire time).
    if (!system && this._shouldQuantizeSeek()) {
      this._scheduleQuantizedSeek(ms);
      return;
    }
    // A system restore is putting back a position READ FROM THE PLAYHEAD, which is already led and
    // already on the current phase. Anchoring it would let an engine swap silently redefine the
    // room's phase, and leading it again would shift the playhead by the offset a second time.
    await this._seekNow(ms, { anchor: !system, applyLead: !system, placed: !system });
  }

  /**
   * Arm a seek to fire ON an ABSOLUTE shared beat. `_scheduleQuantizedSeek` can only compute "the
   * next bar from now", which cannot honour a beat that was decided by another client — a room seek
   * needs this. Reuses the pending-quantized-seek slot so it inherits the whole existing cancel
   * graph (pause, stop, a superseding seek, dispose).
   */
  _scheduleAnchoredSeek(anchorBeat, anchorSourceMs) {
    this._cancelPendingQuantizedSeek();
    this._cancelPendingDriftRealign();
    const delayMs = this._delayMsToSharedBeat(anchorBeat);
    // Already passed (a late-delivered event, or no readable clock) → apply now: the projection at
    // fire time is what makes that the correct catch-up rather than a guess.
    if (!Number.isFinite(delayMs) || delayMs <= 0) {
      // RETURNED, not fired and forgotten: the room runs seeks, starts and stops through one queue
      // per voice, and a seek still landing when the start behind it runs opens the audio at the old
      // position. (The delayed branch below deliberately returns at once — the queue must not block
      // for a whole bar.)
      return this._applyAnchoredSeek(anchorBeat, anchorSourceMs);
    }
    const pending = { timer: null, canceled: false, ms: anchorSourceMs };
    this._pendingQuantizedSeek = pending;
    // The count-in marker blinks at the spot the seek was set to, as a local quantized seek does.
    this._emitCountIn(delayMs, { seekTargetSec: anchorSourceMs / 1000 });
    pending.timer = setTimeout(() => {
      pending.timer = null;
      if (pending.canceled) return;
      if (this._pendingQuantizedSeek === pending) this._pendingQuantizedSeek = null;
      this._clearCountIn();
      void this._applyAnchoredSeek(anchorBeat, anchorSourceMs);
    }, delayMs);
  }

  /**
   * Land a room seek: adopt the ANCHOR, then play the position that anchor PROJECTS at this instant
   * — never the literal number that came over the wire. That is the whole point of sending an
   * anchor: a late delivery, a slow timer, or a tempo change in flight all self-correct, because
   * every client is answering "where does beat-now put me, given that anchor" with the same math.
   */
  async _applyAnchoredSeek(anchorBeat, anchorSourceMs) {
    this.deck.setSeekAnchor?.(anchorBeat, anchorSourceMs);
    // Stopped/paused: there is no live phase to project into, so land on the target itself. The
    // aligned start then comes in through the anchor we just set.
    if (this.playback?.isPlaying?.() !== true || this._sharedClockBeat() === null) {
      // `placed`: a routed seek is still somebody choosing where this deck sits, and every client
      // applying it must treat that spot as the start position — otherwise a room deck's next play
      // snaps away from the spot the room agreed on.
      await this._seekNow(anchorSourceMs, { placed: true });
      return;
    }
    // Resolved from the anchor we just adopted. A target the user placed PAST the loop means "leave
    // the loop", and the anchor carries that: projecting such a seek THROUGH the loop would wrap it
    // straight back inside, which reads as the seek being ignored — the playhead simply keeps looping.
    // ONE resolver answers this here and in the align and correction paths alike, or the three
    // disagree about which timeline the room is on.
    const loopRange = this._phaseLoopRange();
    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    // RAW (un-led) — `_seekNow` applies this device's own output lead.
    const projectedMs = this.deck.computeAlignedSourcePositionMs({ durationMs, loopRange, outputLeadMs: 0 });
    await this._seekNow(Number.isFinite(projectedMs) ? projectedMs : anchorSourceMs, { placed: true });
  }

  /**
   * Quantize a seek while PLAYING and there is a grid to stay in phase with — the joined shared
   * clock, or (with none joined) this deck's OWN running clock (warp on, launch grid set). A seek
   * while stopped/paused has no phase to preserve. (Strictly the clocks — NOT `_shouldQuantizeStart()`
   * whose legacy fallback reads raw adapter peers.)
   */
  _shouldQuantizeSeek() {
    if (!this.playback?.isPlaying?.()) return false;
    return this._sharedClockBeat() !== null || this._ownGridBarDelayMs() !== null;
  }

  /**
   * Wall ms until this deck's OWN next launch-grid boundary — the counterpart of
   * `_computeBarDelayMs` (which reads the shared clock). Needs NO joined shared clock, a RUNNING own
   * clock (playing) and a launch grid; null otherwise. The deck clock's beats live on the source
   * grid and its `secondsPerBeat` is the wall period at the tempo actually playing, so varispeed is
   * respected. Same slack rule as the shared-clock bar delay.
   */
  _ownGridBarDelayMs() {
    if (this._sharedClockBeat() !== null) return null; // joined shared clock → the shared path
    const beatsPerBar = this.deck.launchGridQuarterBeats;
    if (!(beatsPerBar > 0)) return null; // launch grid 'none'
    const clock = this.deck.clock(); // null when not playing / not following anything usable
    if (!clock) return null;
    const beatNow = Number(clock.beatNow);
    const secondsPerBeat = Number(clock.secondsPerBeat);
    if (!Number.isFinite(beatNow) || !(secondsPerBeat > 0)) return null;
    const QUANTIZE_SLACK_MS = 40;
    const EPS = 1e-6;
    const posInBar = ((beatNow % beatsPerBar) + beatsPerBar) % beatsPerBar;
    let beatsToBoundary = beatsPerBar - posInBar;
    if (beatsToBoundary < EPS) beatsToBoundary = beatsPerBar;
    const msPerBar = beatsPerBar * secondsPerBeat * 1000;
    let delayMs = beatsToBoundary * secondsPerBeat * 1000;
    if (delayMs < QUANTIZE_SLACK_MS) delayMs += msPerBar;
    return delayMs;
  }

  _scheduleQuantizedSeek(ms) {
    const shared = this._sharedClockBeat() !== null;
    let targetMs = ms;
    if (!shared) {
      // Own-grid seek: the deck's beat IS its position, so landing mid-bar would shift the groove —
      // snap the target to the nearest own-grid boundary so the jump leaves phase continuous (fires
      // ON a boundary, lands ON a boundary). The count-in marker then blinks at the real landing spot.
      const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
      const snapped = this.deck.snapToOwnGridMs(ms, {
        launchGridQuarterBeats: this.deck.launchGridQuarterBeats,
        durationMs,
        loopRange: this._loopRangeAt(ms),
      });
      if (Number.isFinite(snapped)) targetMs = snapped;
    }
    const delayMs = shared ? this._computeBarDelayMs() : this._ownGridBarDelayMs();
    if (!Number.isFinite(delayMs) || delayMs <= 0) {
      // No valid bar delay (e.g. tempo not resolved) → seek immediately.
      void this._seekNow(targetMs, { anchor: true, placed: true });
      return;
    }
    const pending = { timer: null, canceled: false, ms: targetMs };
    this._pendingQuantizedSeek = pending;
    // Reuse the launch count-in so the UI shows the seek is ARMED — WITH the target position so the
    // waveform blinks a marker at the set spot (the count in the action), per Bruna's UX note.
    this._emitCountIn(delayMs, { seekTargetSec: targetMs / 1000 });
    pending.timer = setTimeout(() => {
      pending.timer = null;
      void this._firePendingQuantizedSeek(pending);
    }, delayMs);
  }

  async _firePendingQuantizedSeek(pending) {
    if (pending.canceled) return;
    this._clearCountIn();
    try {
      await this._seekNow(pending.ms, { anchor: true, placed: true });
    } finally {
      if (this._pendingQuantizedSeek === pending) this._pendingQuantizedSeek = null;
    }
  }

  _cancelPendingQuantizedSeek() {
    // Bumped even with nothing armed: this is the "a seek is no longer wanted" signal, and a seek
    // that has ALREADY fired is mid-await with no pending slot left to cancel.
    this._seekEpoch = (this._seekEpoch ?? 0) + 1;
    const pending = this._pendingQuantizedSeek;
    if (!pending) return;
    pending.canceled = true;
    if (pending.timer) { try { clearTimeout(pending.timer); } catch (_) {} pending.timer = null; }
    this._pendingQuantizedSeek = null;
    this._clearCountIn();
  }

  /**
   * Apply a seek to BOTH clocks. `anchor` marks a seek that establishes new phase (a USER seek):
   * from here the shared beat maps to THIS position, not the grid marker. Without that the drift
   * sampler would read the user's own seek as error and quietly pull the playhead back — the exact
   * failure the anchor model exists to prevent. Corrections pass it as false: they RE-STATE the
   * anchor's projection, so re-anchoring them would just re-round the same phase every few seconds.
   */
  async _seekNow(ms, options = {}) {
    // Serialized per voice. The epoch below stops a superseded seek writing anything FURTHER, but it
    // cannot recall a `transport.seek` already dispatched — so without this, two overlapping seeks
    // could resolve out of order and leave the transport on one position and the playback on
    // another. A superseded seek exits at its first check, so the queue drains immediately.
    // The epoch is claimed HERE, at call time, not when the body finally runs — so a seek queued
    // behind a newer one is already superseded before it starts and skips its writes entirely.
    const epoch = (this._seekEpoch ?? 0) + 1;
    this._seekEpoch = epoch;
    const run = () => this._seekNowBody(ms, epoch, options);
    this._seekChain = (this._seekChain ?? Promise.resolve()).then(run, run);
    return this._seekChain;
  }

  async _seekNowBody(ms, epoch, { anchor = false, applyLead = true, isValid = null, placed = false } = {}) {
    // Every seek supersedes the one before it. Canceling an ARMED timer is not enough: a seek that
    // has already fired is mid-await here, and a pause, stop, dispose, or newer seek arriving during
    // those awaits must be able to stop it writing a position that is no longer wanted.
    // `isValid` lets a CALLER add its own liveness condition (a drift correction dies when the loop
    // topology it was computed against changes). Seeks without one only answer to the seek epoch.
    const superseded = () => this._seekEpoch !== epoch || (isValid !== null && !isValid());
    // The anchor beat is READ here (it must match the target we are about to compute) but COMMITTED
    // only once the position has actually landed: a seek abandoned mid-await must leave no trace, or
    // every later projection runs from a phase the playhead never reached.
    const canAnchor = anchor
      && this._sharedClockBeat() !== null
      && typeof this.deck?.getCurrentSyncBeat === 'function'
      && typeof this.deck?.setSeekAnchor === 'function';
    const anchorBeat = canAnchor ? this.deck.getCurrentSyncBeat() - 1 : null;
    const commitAnchor = () => {
      if (anchorBeat !== null) this.deck.setSeekAnchor(anchorBeat, ms);
    };
    // The phase moved — nothing measured against the old one still holds.
    this._clearDriftCorrection();
    // Carry the per-device manual audio offset through a seek too, so a seek during a synced
    // session keeps this device's output-latency compensation instead of silently dropping it (the
    // seek path does NOT go through _alignWrapPlaybackPosition). Lead the target the SAME way the
    // aligned start does — via the deck (the one owner of that math). Wrap within the loop only when
    // the target lands INSIDE it, else within the track: a target the user placed outside the loop
    // must stay outside. When sync is off or the offset is 0 this is a no-op → `target === ms`.
    const target = applyLead
      ? this._leadSeekPositionMs(ms, { loopRange: this._loopRangeAt(ms) })
      : ms;

    // Only ever a RELEASE up front: a target PAST the loop needs the sink to stop wrapping before the
    // playhead moves, or it folds the new position straight back inside and the seek reads as
    // ignored. The range is only ever disengaged, never cleared — it stays visible, stays in the
    // snapshot, and runs again by the same rule once the playhead can reach it. Engaging is left to
    // the landing below: arming the sink for a target the playhead has not reached would wrap the
    // playhead in if this seek were then superseded.
    if (superseded()) return;
    this._releaseLoopForSeek(target);

    try {
      await this.transport.seek(target);
      if (superseded()) return;
      // Again, immediately before the write: a loop can ARRIVE while we await (a room apply), and it
      // engages from wherever the playhead still is. Writing the target under it would let the sink
      // fold the new position straight into a range the seek is meant to leave.
      this._releaseLoopForSeek(target);
      await this.playback.setPosition(target);
      if (superseded()) return;
      commitAnchor();
      // A seek somebody ASKED for (`placed`) — not a system restore and not a drift correction, which
      // are nobody choosing anything. Landing while nothing plays, it IS where playback begins: the
      // start then honours it exactly instead of nudging it onto the launch grid and counting in
      // first, because "start here" cannot mean "start 300 ms before here, in a bar's time". Landing
      // mid-playback it REPLACES any start position set earlier — an explicit seek supersedes a cue.
      // Recorded as the position that actually LANDED, so a lead or a clamp leaves no phantom mark.
      if (placed) {
        this._startPosition = this.playback?.isPlaying?.() !== true
          ? { rawMs: ms, landedMs: target }
          : null;
      }
      this.mediaSession?.syncPositionState?.();
    } finally {
      // Settle the loop against where the playhead ACTUALLY is. A superseded seek never moved it, so
      // this restores the loop it released for a jump that never happened; a completed one confirms
      // the release. Either way there is no copy to reconcile — the range never left.
      this._resolveLoopEngagement();
    }
  }

  /**
   * Release the loop when `targetMs` is PAST it, so the sink stops wrapping before the playhead is
   * written there — otherwise it folds the new position straight back inside and the seek reads as
   * ignored. Only ever releases: engaging for a position the playhead has not reached yet is the
   * landing's job, since a seek that is then superseded would leave the sink wrapping a playhead
   * that never moved.
   */
  _releaseLoopForSeek(targetMs) {
    if (this._loopEngagesAt(this._enabledLoopRange(), targetMs)) return;
    this._resolveLoopEngagement(targetMs);
  }

  /**
   * Lead a raw seek target (ms) by the manual audio offset, but only while a shared clock is JOINED
   * (the compensation is inaudible with no session to be early against). Delegates the wall-ms→source
   * lead + wrap to the deck owner. Offset 0 / nothing joined → returns `ms` untouched.
   *
   * Same joined-clock predicate as the alignment and the own-grid paths, and for the same reason: a
   * lead applied on a deck riding its OWN grid would push the seek off the very boundary the
   * own-grid snap just landed it on.
   */
  _leadSeekPositionMs(ms, { loopRange = null } = {}) {
    if (this._sharedClockBeat() === null) return ms;
    const outputLeadMs = getManualAudioOffsetMs();
    if (!outputLeadMs) return ms;
    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    // The lead can carry a target BEFORE the track start; `Deck.leadSourcePositionMs` owns what
    // happens then (it answers with the start rather than wrapping round to the end), for this path
    // and for the beat→playhead alignment alike.
    const led = this.deck.leadSourcePositionMs(ms, { durationMs, loopRange, outputLeadMs });
    return Number.isFinite(led) ? led : ms;
  }

  /**
   * Draw (or redraw) the loop. `active` is the ENABLED half of the loop, not "engage now": the
   * range is stored either way, and `_resolveLoopEngagement` decides from the playhead whether it
   * runs. That is what lets a listener adopt the holder's loop, or a user draw one ahead of the
   * playhead, without either being yanked into a range they are nowhere near.
   */
  setLoopRange(startMs, endMs, { active = true, roomRemote = false, system = false } = {}) {
    const nextActive = Boolean(active);
    // Room mode: a non-owner user edit is blocked before it touches audio; an owner edit publishes so
    // every client's copy of this deck gets the same loop. Remote/system writes pass straight through.
    if (this._roomLoopBlocked(roomRemote, system)) return;
    const next = { start: Number(startMs), end: Number(endMs) };
    const rangeMatches = this._sameLoopRange(this._loop, next);
    // The wrap period moved if EITHER half of the loop did — invalidating any armed drift re-align,
    // whose target was computed against the topology this replaces. On/off counts even when the sink
    // never flips (a playhead past the range is held either way), or a correction computed while the
    // loop was off would still be allowed to land after it was turned back on. Re-applying the
    // identical loop must move nothing: a room re-publishes the same snapshot constantly, and each
    // restatement would drop the drift window.
    if (!rangeMatches || this._loopEnabled !== nextActive) this._bumpLoopGeneration();
    // Below the tolerance nothing moves at all — not the stored loop, not the mirrors. Adopting a
    // sub-millisecond nudge while calling it "the same range" is how a drag could shift the wrap
    // period without invalidating a correction armed against the old one. A zero-width or backwards
    // range is not a loop: it CLEARS, rather than silently leaving the previous one standing for the
    // next snapshot to republish — two clients applying the same edit have to end up in the same place.
    if (!rangeMatches) {
      const usable = Number.isFinite(next.start) && Number.isFinite(next.end) && next.end > next.start;
      this._loop = usable ? next : null;
    }
    this._loopEnabled = nextActive;
    this._resolveLoopEngagement();
    // Owner edit → publish the range to the room (skipped for remote applies + system writes). The
    // range the SINK took, not the one asked for: it may have been rounded or clamped to the decoded
    // buffer, and publishing the raw request would leave every listener on a slightly different loop.
    if (this._roomLoop && !roomRemote && !system) {
      if (this._loop) this._roomLoop.publishRange?.(this._loop.start, this._loop.end, nextActive);
      else this._roomLoop.publishClear?.(); // nothing usable was drawn — say so, don't leave peers guessing
    }
  }

  /** Turn the loop on/off without touching its range. On means "loop this range when the playhead
   *  is in it" — a loop enabled while the playhead is elsewhere waits, it does not jump. */
  setLoopEnabled(enabled, { roomRemote = false, system = false } = {}) {
    const active = Boolean(enabled);
    if (this._roomLoopBlocked(roomRemote, system)) return;
    // On/off moves the wrap topology even when the sink's engagement does not — see `setLoopRange`.
    if (this._loopEnabled !== active) this._bumpLoopGeneration();
    this._loopEnabled = active;
    this._resolveLoopEngagement();
    // Owner toggle → publish the enable/disable to the room (skipped for remote applies + system).
    if (this._roomLoop && !roomRemote && !system) {
      this._roomLoop.publishEnable?.(active);
    }
  }

  clearLoop({ roomRemote = false, system = false } = {}) {
    if (this._roomLoopBlocked(roomRemote, system)) return;
    this._bumpLoopGeneration(); // back to folding against the whole track
    this._loop = null;
    this._loopEnabled = false;
    this.transport.clearLoop();
    this.playback.clearLoop();
    // Owner cleared the loop → tell the room to clear it too (skipped for remote applies + system).
    if (this._roomLoop && !roomRemote && !system) {
      this._roomLoop.publishClear?.();
    }
  }

  /**
   * The loop this voice LOGICALLY has — the range the user drew and whether it is on, never the
   * momentary engine state. A loop the playhead has left is HELD, not gone: publishing "no loop"
   * in that window tells every other client to delete the loop for good, and they have no way to
   * know it was only waiting for the playhead to come back.
   * @returns {{startMs: number, endMs: number, enabled: boolean}|null}
   */
  getSharedLoopState() {
    const range = this._loop;
    if (!Number.isFinite(range?.start) || !Number.isFinite(range?.end)) return null;
    return { startMs: range.start, endMs: range.end, enabled: this._loopEnabled === true };
  }

  async _handlePlaybackStopped() {
    console.debug('[PlaybackSync] _handlePlaybackStopped fired', {
      state: this._playbackState,
      playerPlaying: this.playback?.isPlaying?.(),
      audioPaused: this.playback?.audio?.paused,
    });
    const positionMs = typeof this.playback?.getCurrentPositionMs === 'function'
      ? this.playback.getCurrentPositionMs()
      : 0;
    await this.transport.pause();
    await this.transport.seek(positionMs);
    this._updatePlaybackState('stopped', { source: 'player-stop' });
  }

  _handlePlaybackBuffering(payload = {}) {
    const isBuffering = Boolean(payload?.isBuffering);
    const bufferedAheadMs = Number(payload?.bufferedAheadMs);
    const readyState = Number(payload?.readyState);
    this._bufferingState = {
      isBuffering,
      bufferedAheadMs: Number.isFinite(bufferedAheadMs) ? bufferedAheadMs : 0,
      readyState: Number.isFinite(readyState) ? readyState : 0,
      source: payload?.reason || 'streaming',
      timestamp: Number(payload?.timestamp) || (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    };

    if (isBuffering) {
      this._updatePlaybackState('buffering', { transient: true, source: 'streaming-buffering' });
      return;
    }
    this._updatePlaybackState(this._playbackState, { transient: true, source: 'streaming-buffering-end' });
  }

  setActiveDimension(dimensionId) {
    if (!dimensionId) {
      console.warn('[AudioEngineAdapter.setActiveDimension] No dimensionId provided');
      return;
    }

    this._effectsMeta.activeDimensionId = dimensionId;
    const cachedConfig = this._getCachedEffectsConfig(dimensionId) || this._cloneEffectsConfig();
    const chain = this._ensureDimensionChain(dimensionId, cachedConfig);
    this.effectRacks = chain
      ? {
          x: chain.axisRacks.x ?? null,
          y: chain.axisRacks.y ?? null,
          z: chain.axisRacks.z ?? null,
        }
      : { x: null, y: null, z: null };
    this.playback.effectRacks = this.effectRacks;
    // The streaming backend has no `player`, so it is wired by handing it an input node rather than
    // by _rewireGlobalChain's player branch. Hand it the INPUT GAIN, not the first effect: pointing
    // it at the effects chain skips the user's input trim entirely, so on a streamed track the IN
    // fader would do nothing and its meter would sit at zero.
    this.playback.setInputNode?.(this.inputLevelGain ?? chain?.inputGain ?? null);
    this.playback.setEffectRacks?.(this.effectRacks, this.axisOrder);
  }

  // Public getter used by monitorUtils/rack display filtering
  getActiveDimensionId() {
    return this._effectsMeta?.activeDimensionId ?? null;
  }

  /**
   * Snapshot of the engine monitor across ALL dimensions (not just the active one).
   * Reads each dimension's per-axis racks via {@link EffectsRack#getMonitorReadout}, so the
   * value math has a single owner. Drives the React Engine Monitor.
   *
   * @param {((dimensionId: string, axis: string) => (number|null))} [getNormalized] Optional source
   *   for the REAL normalized (0–1) input per (dimension, axis) — pass ParameterManager's live
   *   normalized value so the monitor reflects the true parameter state (incl. at load, before audio
   *   drives the racks) instead of each rack's last-applied/preset-default `controlNormalized`.
   * @returns {{
   *   activeDimensionId: (string|null),
   *   dimensions: Array<{
   *     dimensionId: string,
   *     dimensionLabel: string,
   *     axes: Object<string, Array<{slot:string,label:(string|null),value:(number|null),units:(string|null),formatted:string}>>
   *   }>
   * }}
   */
  getMonitorSnapshot(getNormalized = null) {
    const activeDimensionId = this.getActiveDimensionId();
    const dimensions = [];

    this._dimensionOrder.forEach((dimensionId) => {
      const chain = this._dimensionChains.get(dimensionId);
      if (!chain || !chain.axisRacks) return;

      const axes = {};
      let dimensionLabel = dimensionId;
      this.axisOrder.forEach((axis) => {
        const rack = chain.axisRacks[axis];
        if (!rack || typeof rack.getMonitorReadout !== 'function') {
          axes[axis] = [];
          return;
        }
        if (rack.dimensionLabel) dimensionLabel = rack.dimensionLabel;
        const normalized = typeof getNormalized === 'function' ? getNormalized(dimensionId, axis) : null;
        axes[axis] = rack.getMonitorReadout(Number.isFinite(normalized) ? normalized : null);
      });

      dimensions.push({ dimensionId, dimensionLabel, axes });
    });

    return { activeDimensionId, dimensions };
  }

  setParameter(paramName, value, dimensionId = null, context = {}) {
    if (!paramName) return;

    if (paramName === PREMIX_PARAM) {
      this._handleBodyLevelParameter(value);
      return;
    }

    if (paramName === INPUT_GAIN_PARAM) {
      this._handleInputLevelParameter(value);
      return;
    }

    if (!AXES.includes(paramName)) {
      console.warn('[AudioEngineAdapter.setParameter] Unsupported parameter', { paramName });
      return;
    }

    const targetDimensionId = dimensionId || this._effectsMeta.activeDimensionId;
    if (!targetDimensionId) {
      console.warn('[AudioEngineAdapter.setParameter] Missing dimension context for axis', {
        paramName,
        dimensionId,
      });
      return;
    }

    // An axis write is a VALUE write — the chain it lands on is already built and already carries
    // the current effects config (`updateEffectsConfig`/`setActiveDimension` own that). Re-ensuring
    // it here re-ran `setPerformanceProfile` + `configure` on all three racks on EVERY knob move,
    // LFO tick and incoming room parameter — re-attaching automation bridges and re-running
    // `module.configure()`/`applyValue()` thousands of times a minute. Build only when the
    // dimension has never been touched.
    const chain = this._dimensionChains.get(targetDimensionId)
      || this._ensureDimensionChain(targetDimensionId, this._getCachedEffectsConfig(targetDimensionId));
    const rack = chain?.axisRacks?.[paramName];
    if (!rack) {
      console.warn('[AudioEngineAdapter.setParameter] Rack not found for axis/dimension', {
        axis: paramName,
        dimensionId: targetDimensionId,
      });
      return;
    }

    let cosmicLfo = this._cosmicLfoCache?.[paramName] ?? null;
    if (!cosmicLfo && !this._cosmicManager) {
      this.refreshCosmicLfoCache();
      cosmicLfo = this._cosmicLfoCache?.[paramName] ?? null;
    }
    if (cosmicLfo && cosmicLfo.isActive && typeof cosmicLfo.isAudioConnected === 'function' && cosmicLfo.isAudioConnected()) {
      debugParamRoute('engine:axis-skip-cosmic', { axis: paramName, dimensionId: targetDimensionId });
      return;
    }

    const normalized = typeof this.userManager?.getNormalizedValue === 'function'
      ? this.userManager.getNormalizedValue(paramName, targetDimensionId)
      : undefined;

    const fallback = Number.isFinite(Number(normalized)) ? Number(normalized) : Number(value);
    const clamped = Number.isFinite(fallback) ? Math.max(0, Math.min(1, fallback)) : 0;

    const discreteKey = `${paramName}:${targetDimensionId}`;
    const supportsContinuous = typeof rack.supportsContinuousAutomation === 'function'
      ? rack.supportsContinuousAutomation()
      : true;

    if (!supportsContinuous) {
      const last = this._lastDiscreteValues?.[discreteKey];
      if (last !== null && last !== undefined && Math.abs(last - clamped) < 0.05) {
        return;
      }
      this._lastDiscreteValues[discreteKey] = clamped;
    } else if (this._lastDiscreteValues && this._lastDiscreteValues[discreteKey] !== null) {
      this._lastDiscreteValues[discreteKey] = null;
    }

    debugParamRoute('engine:apply-axis', { axis: paramName, dimensionId: targetDimensionId, clamped, value });

    try {
      if (typeof window !== 'undefined' && window.__DEBUG_AXIS_X_DIM3 && paramName === 'x' && targetDimensionId === 'EW::III') {
        
      }
    } catch (_) {}

    rack.applyInputValue(clamped, {
      axis: paramName,
      dimensionId: targetDimensionId,
      rawValue: value,
      updateIntent: context?.updateIntent === 'commit' ? 'commit' : 'live',
      sourceController: context?.sourceController ?? null,
      reason: context?.reason ?? 'parameter-change',
    });
  }

  /**
   * (Re)build the gain-rail level probes. Each hangs off the OUTPUT of the gain stage it
   * reports, so the reading is what the user's fader actually produced: `input` is
   * post-input-trim / pre-effects, `output` is post-premix-fader. Taps are read-only
   * branches — they never sit in the signal path, so a failed tap costs a dead meter, not
   * silence.
   */
  _createLevelTaps() {
    this._disposeLevelTaps();
    const context = Tone.getContext?.()?.rawContext ?? Tone.context?.rawContext ?? null;
    if (!context) return;
    this._inputLevelTap = createLevelTap(context, this.inputLevelGain, { detector: 'peak', holdDecay: 0 });
    this._outputLevelTap = createLevelTap(context, this.bodyLevelGain, { detector: 'peak', holdDecay: 0 });
  }

  _disposeLevelTaps() {
    this._inputLevelTap?.dispose?.();
    this._inputLevelTap = null;
    this._outputLevelTap?.dispose?.();
    this._outputLevelTap = null;
  }

  /**
   * Linear peak level (0..1 unclipped, above 1 when hot) at one of the two user gain stages.
   * Polled by the meter's animation frame — it must stay allocation-free and never throw.
   * @param {'input'|'output'} channel
   */
  getChannelLevel(channel) {
    const tap = channel === 'input' ? this._inputLevelTap : this._outputLevelTap;
    return tap ? tap.read() : 0;
  }

  getAmplitude() {
    if (!this.masterMeter) return 0;
    const meterValue = this.masterMeter.getValue();
    const db = Array.isArray(meterValue) ? meterValue[0] : meterValue;
    if (!Number.isFinite(db)) return 0;
    return Tone.dbToGain ? Tone.dbToGain(db) : Math.pow(10, db / 20);
  }

  isPlaying() {
    return typeof this.playback?.isPlaying === 'function'
      ? this.playback.isPlaying()
      : false;
  }

  getCurrentPositionMs() {
    let position = 0;
    if (typeof this.playback?.getCurrentPositionMs === 'function') {
      position = this.playback.getCurrentPositionMs();
    } else if (typeof this.transport?.getCurrentPositionMs === 'function') {
      position = this.transport.getCurrentPositionMs();
    }
    return position;
  }

  getDurationMs() {
    return typeof this.playback?.getDurationMs === 'function'
      ? this.playback.getDurationMs()
      : 0;
  }

  /** Decoded source buffer for source-level engines (granular). Null while
   *  streaming, before load, or when the active sink doesn't retain one. */
  getDecodedAudioBuffer() {
    return this.playback?.getDecodedBuffer?.() ?? null;
  }

  /** The bus where source-level engine output joins the dry player signal
   *  (before the effects chain), so both take the same voice path. */
  getSourceMixBus() {
    return this.normalizationGain ?? null;
  }

  /** Dry-leg level (0–1) for a source-level engine's wet/dry crossfade. */
  setSourceDryLevel(level) {
    this.playback?.setSourceDryLevel?.(level);
  }

  /** Acquire this voice's source-level engine for a family (first acquire builds it, last
   *  release disposes it) — see sourceEngineHost.js for the build contract. */
  acquireSourceEngine(id, build) {
    return this._sourceEngines.acquire(id, build);
  }

  /** The family's live source engine, if one exists right now. Does not create or refcount. */
  peekSourceEngine(id) {
    return this._sourceEngines.peek(id);
  }

  /** Observe source-engine lifetime: `cb(id, engine)` on create, `cb(id, null)` on dispose.
   *  Observers (the visual seam) must never extend an engine's refcount. */
  observeSourceEngines(cb) {
    return this._sourceEngines.observe(cb);
  }

  /** Every live rack-effect slot across ALL dimension chains (the chains run in
   *  series and are all always audible, so consumers must not filter by the
   *  active dimension). Classify by `slot.config.effectId`. */
  peekEffectSlots() {
    const slots = [];
    this._dimensionChains.forEach((chain) => {
      Object.values(chain?.axisRacks ?? {}).forEach((rack) => {
        rack?.slots?.forEach((slot) => {
          if (slot) slots.push(slot);
        });
      });
    });
    return slots;
  }

  /** Observe rack-effect slot lifetime across all dimension chains: `cb(slot, true)`
   *  on create, `cb(slot, false)` right before dispose (the visual seam — same
   *  contract as `EffectsRack.observeSlots`). Racks built after subscription are
   *  covered; racks only relay while this adapter lives. */
  observeEffectSlots(cb) {
    if (typeof cb !== 'function') return () => {};
    this._effectSlotObservers.add(cb);
    return () => {
      this._effectSlotObservers.delete(cb);
    };
  }

  _notifyEffectSlot(slot, present) {
    for (const cb of this._effectSlotObservers) {
      try {
        cb(slot, present);
      } catch (error) {
        console.warn('[AudioEngineAdapter] Effect-slot observer failed', error);
      }
    }
  }

  isLooping() {
    return typeof this.playback?.isLooping === 'function'
      ? this.playback.isLooping()
      : false;
  }

  /**
   * The loop state for the UI: is there a loop, and is it ON. Deliberately NOT `isLooping()` — a
   * loop the playhead has not reached yet is on and waiting, and reading engagement here would blink
   * the loop button off and make a held loop look deleted every time the playhead steps outside it.
   */
  isLoopActive() {
    return this._loopEnabled === true && this.hasLoopRange();
  }

  hasLoopRange() {
    return this._loop !== null;
  }

  /** The loop range the user drew, engaged or not — read from the adapter, not the sink, so a
   *  backend swap (which disposes the sink's copy) can never make the markers blink out. */
  getLoopRange() {
    return this._loop ? { start: this._loop.start, end: this._loop.end } : null;
  }

  /**
   * The current track's waveform JSON url (audiowaveform v2) — the SAME field
   * PeaksView reads to fetch its peaks, surfaced so the React timeline kit can fetch and
   * render the exact same data without going through Peaks. Resolves from the playback
   * track data (same precedence waveformMount uses), then the documented url fields.
   */
  getWaveformUrl() {
    const td = this.playbackTrackData || this.trackData?.track || this.trackData || null;
    return td?.waveformJSONURL || td?.waveformURL || td?.waveformJsonUrl || null;
  }

  setPlaybackRate(rate, options = {}) {
    if (this._speedControlDisabled) {
      return;
    }
    if (typeof this.playback?.setPlaybackRate === 'function') {
      this.playback.setPlaybackRate(rate, options);
      this.mediaSession?.syncPositionState?.();
    }
  }

  getPlaybackRate() {
    return typeof this.playback?.getPlaybackRate === 'function'
      ? this.playback.getPlaybackRate()
      : 1;
  }

  /** How the sink interprets rate: 'stretch' (pitch locked) or 'varispeed'
   *  (tape). Returns false when the sink has no stretch engine. */
  setRateMode(mode) {
    return this.playback?.setRateMode?.(mode) === true;
  }

  getRateMode() {
    return typeof this.playback?.getRateMode === 'function'
      ? this.playback.getRateMode()
      : 'varispeed';
  }

  /** Pitch shift with tempo locked (stretch engine only). Returns false when
   *  unhandled so callers can fall back to tape mappings. */
  setPitchSemitones(semitones) {
    return this.playback?.setPitchSemitones?.(semitones) === true;
  }

  getPitchSemitones() {
    return typeof this.playback?.getPitchSemitones === 'function'
      ? this.playback.getPitchSemitones()
      : 0;
  }

  getGridMarkerState() {
    return this.deck;
  }

  /** This voice's OWN native track tempo (its grid reference, never the shared session/singleton) —
   *  the uncontaminated per-voice value a scoped persist should fall back to when a caller only
   *  means to update a different field (e.g. meter) and mustn't accidentally overwrite this voice's
   *  saved tempo with whatever the shared coordinator currently holds. */
  getOwnTrackBpm() {
    return this.deck.nativeTempo;
  }

  /** Mirror the deck's transport tempo into the `sync-bpm` readout — the number IS `deck.tempo`
   *  (master-driven while synced, the deck's own while unsynced, held across sync toggles). Tagged
   *  as the display mirror so the edit bridge ignores the echo. */
  _refreshSyncBpmReadout() {
    const bpm = this.deck.tempo;
    if (Number.isFinite(bpm) && bpm > 0) {
      try { this.userManager?.setRawValue?.('sync-bpm', bpm, SYNC_BPM_DISPLAY_SOURCE); } catch (_) {}
    }
  }

  /** Mirror the deck's NATIVE (audiofile) tempo into the `sync-track-bpm` readout. The deck owns the
   *  value; this is the one place the panel's number is repainted from it, so a change this client did
   *  not make — a room peer's edit — shows up here too instead of leaving the readout on a tempo the
   *  audio has stopped using. A system write (no source controller): it is a repaint, not an edit,
   *  so the room's parameter write gate passes it and no publish path sees it. */
  _refreshTrackBpmReadout() {
    const bpm = this.deck.nativeTempo;
    if (!Number.isFinite(bpm) || bpm <= 0) return;
    if (!this.userManager?.getParameter?.('sync-track-bpm')) return;
    try { this.userManager.setRawValue('sync-track-bpm', bpm); } catch (_) {}
  }

  /** Set THIS voice's own native track tempo (a per-track edit from the kit panel — the persisted
   *  value): the deck updates its grid reference + follow rate WITHOUT touching the shared
   *  coordinator, so one voice's tempo edit never retunes a synced sibling.
   *  @param {number} trackBpm
   *  @param {{ roomRemote?: boolean }} [options] `roomRemote` marks an apply of authoritative room
   *    state — the deck neither gates it nor publishes it back.
   *  @returns {boolean} false only when the room refused the write (the commit seam skips its persist). */
  setOwnTrackBpm(trackBpm, { roomRemote = false } = {}) {
    return this.deck.setNativeTempo(trackBpm, { roomRemote });
  }

  getGridMarkers() {
    return this.deck.getGridMarkers();
  }

  getGridMarker() {
    return this.deck.getGridMarker();
  }

  getGridMarkerTimeSec() {
    return this.deck.getGridMarkerTimeSec();
  }

  /**
   * Updates the source-audio time used as the current grid marker for sync alignment.
   *
   * The FORCED re-align is why a room's remote downbeat apply comes through here rather than straight
   * to the deck: the deck's own change stream only re-aligns while stopped, so a marker moved mid-play
   * would leave this client's playhead on an origin the projection has left.
   *
   * @param {number} value
   * @param {{ roomRemote?: boolean }} [options] `roomRemote` marks an apply of authoritative room
   *   state — the deck neither gates it nor publishes it back.
   * @returns {boolean} false only when the room refused the write, so the caller can skip the persist
   *   that normally follows (see `commitGridMarkerFromUi`).
   */
  setGridMarkerTimeSec(value, { roomRemote = false } = {}) {
    // A refused write moved nothing, so it must not re-align either.
    if (this.deck.setGridMarkerTimeSec(value, { roomRemote }) === false) return false;
    if (syncCoordinator?.isEnabled === true) {
      this._alignWrapPlaybackPosition({ force: true }).catch((error) => {
        console.warn('[AudioEngineAdapter] Failed to update grid marker.', error);
      });
    }
    return true;
  }

  getWrapGridState() {
    return this.getGridMarkerState();
  }

  setWrapGridStartTimeSec(value) {
    this.setGridMarkerTimeSec(value);
  }

  getWrapGridStartTimeSec() {
    return this.getGridMarkerTimeSec();
  }

  isSpeedControlDisabled() {
    return Boolean(this._speedControlDisabled);
  }

  getSpeedControlState() {
    return {
      disabled: Boolean(this._speedControlDisabled),
      reason: this._speedControlDisableReason,
      strategy: this.playback?.getDecodeStrategy?.() || null,
    };
  }

  /** Whether a user-requested buffered reload is currently in flight (UI reads this for a busy state). */
  isBufferedReloadPending() {
    return this._bufferedReloadPromise != null;
  }

  /**
   * "Unlock speed": a conscious user overrides the adaptive streaming decision and forces this
   * track into the buffered/RAM backend — the mobile speed lock exists only because streaming
   * can't stretch, so a successful buffered load lifts it. Automatic streaming stays the default;
   * this is the explicit escape hatch.
   *
   * Order matters: swap → AWAIT THE REAL DOWNLOAD/DECODE → only then re-run the strategy owner to
   * lift the lock. Declaring success at swap time would unlock speed for an asset that may still
   * fail to decode. On failure everything reverts: streaming backend back, lock re-engaged (via
   * the same one owner), position kept. Single-flight: repeat calls join the in-flight attempt.
   * Playback parity with the effects-driven engine swap: we stop first and stay stopped — the
   * user restarts (a forced mid-play backend swap can't be seamless).
   * @returns {Promise<boolean>} true when buffered playback is live and speed is unlocked.
   */
  async requestBufferedReload() {
    if (this._bufferedReloadPromise) return this._bufferedReloadPromise;
    // Watchdog: the attempt must ALWAYS settle. A hung await inside (a worklet
    // RPC that never answers, a decode that never returns) would otherwise
    // leave the single-flight promise set forever — every retry tap silently
    // no-ops and the user sees nothing. On timeout: report failure, revert to
    // streaming, and free the single-flight slot so a retry is possible. The
    // attempt TOKEN fences the orphan: a timed-out attempt that resumes later
    // finds its token stale and bails before touching playback state (a late
    // seek / strategy re-resolve would otherwise fight the next retry).
    const token = Symbol('buffered-reload-attempt');
    this._bufferedReloadToken = token;
    let watchdogTimer = null;
    const watchdog = new Promise((resolve) => {
      watchdogTimer = setTimeout(() => resolve(RELOAD_WATCHDOG_TIMEOUT), BUFFERED_RELOAD_WATCHDOG_MS);
    });
    const attempt = this.#performBufferedReload(token)
      .catch((error) => {
        // Belt for a throw outside the attempt's own try (nothing should, but
        // a reject here must still settle as a reported failure, not silence).
        console.warn('[AudioEngineAdapter] Buffered reload rejected unexpectedly:', error);
        return false;
      });
    this._bufferedReloadPromise = Promise.race([attempt, watchdog])
      .then(async (outcome) => {
        if (outcome !== RELOAD_WATCHDOG_TIMEOUT) return outcome;
        console.warn('[AudioEngineAdapter] Buffered reload timed out — reverting to streaming.');
        this._bufferedReloadToken = null; // fence the orphaned attempt out
        this._forcePrebuffer = false;
        try {
          // In a shared session there is nothing to revert TO: dropping this voice onto the
          // streaming sink is the divergence the session is meant to prevent. The attempt just
          // reports failure and the voice stays where it is.
          if (!this._multiplayer && this.playback?.getDecodeStrategy?.() !== 'stream') {
            await this.#swapPlayback('stream');
          }
          this.#resolveDecodeStrategy(); // re-engages the lock through the one owner
        } catch (revertError) {
          console.error('[AudioEngineAdapter] Failed to revert to streaming after reload timeout:', revertError);
        }
        return false;
      })
      .finally(() => {
        clearTimeout(watchdogTimer);
        this._bufferedReloadPromise = null;
      });
    return this._bufferedReloadPromise;
  }

  async #performBufferedReload(token) {
    // Stale-token fence: true only while THIS attempt still owns the reload.
    const owns = () => this._bufferedReloadToken === token;
    const bufferedStrategies = ['prebuffer', 'stretch'];
    if (bufferedStrategies.includes(this.playback?.getDecodeStrategy?.()) && !this._speedControlDisabled) {
      return true; // already buffered + unlocked — nothing to do
    }
    // No feasibility refusal here: the unlock is an explicit user choice and
    // always gets its attempt, whatever the device — a failed load reverts
    // cleanly and reports through the failure toast.
    // Capture BEFORE stopping (stop can rewind the readhead).
    const positionMs = this.getCurrentPositionMs();
    this._forcePrebuffer = true;
    try {
      if (this.playback?.isPlaying?.() || this.transport?.isRunning) {
        // Stop THIS voice — the unlock is voice-scoped (a collection tile), so never reach for the
        // ACTIVE voice's transport: that can stop a sibling tile and leave this one running. Use
        // this adapter's own registry entry (its transport control keeps the play button honest),
        // else stop the engine directly.
        const entry = voiceRegistry.all().find((v) => v?.audioEngine === this) ?? null;
        const transport = entry?.transportControl ?? null;
        if (transport) await transport.stop();
        else await this.stop();
      }
      if (!owns()) return false;
      this._pendingDecodeStrategy = null;
      // Ask the resolver which sink a forced-buffered resolve WOULD pick (a
      // pure probe — running the strategy owner itself here would lift the
      // speed lock before the download/decode has actually succeeded).
      const probe = resolvePlaybackStrategy({
        profile: this.performanceProfile,
        effectsConfig: this.engineConfig?.effects,
        trackData: this.playbackTrackData,
        forceBuffered: true,
        multiplayer: this._multiplayer,
      });
      await this.#swapPlayback(probe.sink);
      if (!owns()) return false;
      // The swap only builds the backend — the download/decode IS the feature. Await it.
      if (!this.playback.isLoaded) {
        await this.playback.load();
      }
      if (!owns()) return false;
      if (Number.isFinite(positionMs) && positionMs > 0) {
        try { await this.seekToMilliseconds(positionMs, { system: true }); } catch (_) {}
      }
      if (!owns()) return false;
      // Buffered audio is truly live — NOW let the one strategy owner lift the lock (it resolves
      // a buffered strategy under the sticky override, so the mobile-stream lock no longer applies).
      this.#resolveDecodeStrategy();
      debugEngine('buffered reload complete — engine features unlocked');
      return true;
    } catch (error) {
      console.warn('[AudioEngineAdapter] Buffered reload failed:', error);
      if (!owns()) return false; // the timeout path already reverted for us
      this._forcePrebuffer = false;
      try {
        // A shared session has no streaming fallback by design — see the timeout path above.
        if (!this._multiplayer && this.playback?.getDecodeStrategy?.() !== 'stream') {
          await this.#swapPlayback('stream');
          if (Number.isFinite(positionMs) && positionMs > 0) {
            try { await this.seekToMilliseconds(positionMs, { system: true }); } catch (_) {}
          }
        }
        this.#resolveDecodeStrategy(); // re-engages the lock through the same owner
      } catch (revertError) {
        console.error('[AudioEngineAdapter] Failed to revert to streaming after buffered reload failure:', revertError);
      }
      return false;
    }
  }

  /**
   * Get the playback rate parameter (Tone.Signal) for audio-rate modulation.
   * Used by effects that need to modulate playback speed continuously.
   * @returns {Tone.Signal|null} Signal if available (prebuffer mode), null otherwise (streaming mode)
   */
  getPlaybackRateParam() {
    return typeof this.playback?.getPlaybackRateParam === 'function'
      ? this.playback.getPlaybackRateParam()
      : null;
  }

  /**
   * Get the reverse parameter (Tone.Signal) for audio-rate reverse control.
   * Used by effects that need to control playback direction continuously.
   * @returns {Tone.Signal|null} Signal if available (prebuffer mode), null otherwise (streaming mode)
   */
  getReverseParam() {
    return typeof this.playback?.getReverseParam === 'function'
      ? this.playback.getReverseParam()
      : null;
  }

  /**
   * Check if playback is currently in reverse mode.
   * @returns {boolean} True if playing in reverse, false otherwise
   */
  isPlaybackReverse() {
    return typeof this.playback?.isPlaybackReverse === 'function'
      ? this.playback.isPlaybackReverse()
      : false;
  }

  /**
   * Set playback direction (forward or reverse).
   * Only supported in prebuffer mode with compatible effects.
   * @param {boolean} reverse - True for reverse playback, false for forward
   * @param {object} options - Optional playback options
   * @returns {Promise<void>}
   */
  async setPlaybackReverse(reverse, options = {}) {
    const desired = Boolean(reverse);
    if (typeof this.playback?.setPlaybackReverse === 'function') {
      await this.playback.setPlaybackReverse(desired, options);
      // Which edge of a loop the playhead is travelling toward just changed, and that is half the
      // engagement rule: a loop the playhead had passed is reachable again going the other way, and
      // one it was approaching is now behind it.
      this._resolveLoopEngagement();
    }
  }

  /**
   * Update the effects configuration with new axis settings.
   * This method is async to handle automatic playback strategy changes.
   *
   * IMPORTANT: When a module requires prebuffer mode (e.g., reverse effects),
   * this method will automatically swap from streaming to prebuffer BEFORE
   * configuring the effects, ensuring the correct backend is available.
   *
   * @param {object} effectsConfig - Effects configuration with axis settings
   * @returns {Promise<void>}
   */
  async updateEffectsConfig(effectsConfig = {}) {
    const { __meta: meta = {}, ...axesConfig } = effectsConfig || {};
    const fallbackDimensionId = meta.activeDimensionId
      ?? this._effectsMeta.activeDimensionId
      ?? DEFAULT_DIMENSION_ID;

    if (!fallbackDimensionId) {
      console.warn('[AudioEngineAdapter.updateEffectsConfig] Missing dimensionId');
      return;
    }

    if (Array.isArray(meta.dimensionOrder)) {
      this._setDimensionOrder(meta.dimensionOrder);
    }

    const baseConfig = this._cloneEffectsConfig(axesConfig);

    // Check if playback strategy needs to change BEFORE configuring effects.
    // This ensures reverse effects get prebuffer mode before being mounted.
    const desiredStrategy = this.#resolveDecodeStrategy(this.performanceProfile, baseConfig);
    const currentStrategy = typeof this.playback?.getDecodeStrategy === 'function'
      ? this.playback.getDecodeStrategy()
      : null;

    if (currentStrategy && currentStrategy !== desiredStrategy) {
      if (this.playback?.isPlaying?.() || this.transport?.isRunning) {
        // CRITICAL: Some effects (like reverse) require a different playback engine.
        // We cannot apply them while audio is playing, so we must stop playback first,
        // swap the engine, then allow the user to restart.
        if (process.env.NODE_ENV === 'development') {
          console.info('[AudioEngineAdapter] Stopping playback to apply engine strategy change.', {
            currentStrategy,
            desiredStrategy,
            reason: 'effect-requires-different-engine',
          });
        }

        // Store current position for potential resume
        const currentPositionMs = this.getCurrentPositionMs();

        // Use the active voice's transport control to stop (updates button too)
        const transport = voiceRegistry.getActive()?.transportControl ?? null;
        if (transport) {
          await transport.stop();
        } else {
          await this.stop();
        }

        // Now swap the playback strategy
        this._pendingDecodeStrategy = null;
        await this.#swapPlayback(desiredStrategy);

        // Restore position after engine swap
        if (Number.isFinite(currentPositionMs) && currentPositionMs > 0) {
          try {
            await this.seekToMilliseconds(currentPositionMs, { system: true });
          } catch (error) {
            console.warn('[AudioEngineAdapter] Failed to restore position after engine swap:', error);
          }
        }
      } else {
        // Swap playback strategy immediately and AWAIT completion.
        // This blocks effect configuration until the correct backend is ready.
        this._pendingDecodeStrategy = null;
        await this.#swapPlayback(desiredStrategy);
      }
    }

    const discoveredIds = this._collectDimensionIdsFromEffectsConfig(baseConfig);
    if (!discoveredIds.size) {
      discoveredIds.add(fallbackDimensionId);
    } else {
      discoveredIds.add(fallbackDimensionId);
    }

    const configsByDimension = new Map();
    discoveredIds.forEach((dimensionId) => {
      const scopedConfig = this._ensureEffectsConfigDimension(baseConfig, dimensionId);
      configsByDimension.set(dimensionId, {
        next: scopedConfig,
        previous: this.effectsConfigByDimension.get(dimensionId),
      });
      this._dimensionIds.add(dimensionId);
    });

    this._registerDimensionParameters(discoveredIds);

    let topologyChanged = false;
    discoveredIds.forEach((dimensionId) => {
      const record = configsByDimension.get(dimensionId);
      if (!record) return;
      const scopedConfig = record.next;
      const previousConfig = record.previous;
      const changedAxes = this._getChangedAxes(previousConfig, scopedConfig);
      let chain = this._dimensionChains.get(dimensionId);

      if (!chain) {
        chain = this._ensureDimensionChain(dimensionId, scopedConfig);
        topologyChanged = true;
      } else if (changedAxes.length) {
        chain = this._ensureDimensionChain(dimensionId, scopedConfig, changedAxes);
        // Axes changed may or may not affect node graph; EffectsRack.configure will now avoid rewiring
      }

      this.effectsConfigByDimension.set(dimensionId, scopedConfig);

      if (dimensionId === fallbackDimensionId && chain) {
        this.effectRacks = {
          x: chain.axisRacks?.x ?? null,
          y: chain.axisRacks?.y ?? null,
          z: chain.axisRacks?.z ?? null,
        };
        this.playback.effectRacks = this.effectRacks;
      }
    });

    this._effectsMeta.activeDimensionId = fallbackDimensionId;
    if (topologyChanged) {
      this._rewireGlobalChain();
    }

    // Update engineConfig after successful strategy change and effects configuration
    this.engineConfig = {
      ...(this.engineConfig || {}),
      effects: {
        ...baseConfig,
      },
    };

    // Re-sync parameter locks now that the NEW config is installed: the resolve
    // above ran against the candidate config while the lock targets were still
    // scanned from the previous one, so a freshly added engine-requiring module
    // would otherwise miss its lock (and a removed one would stay locked).
    this.#syncParameterLocks();

    debugParamRoute('engine:updateEffectsConfig:applied', {
      dimensionId: fallbackDimensionId,
      dimensionIds: Array.from(this._dimensionIds),
    });
  }

  dispose() {
    this._cancelPendingQuantizedStart();
    this._cancelPendingQuantizedSeek();
    this._cancelPendingRoomStop();
    this._stopDriftSampler();
    this._roomTransport = null;
    this._roomLoop = null;
    if (this._playbackStopSubscription) {
      try {
        this._playbackStopSubscription();
      } catch (_) {}
      this._playbackStopSubscription = null;
    }
    if (this._playbackBufferingSubscription) {
      try {
        this._playbackBufferingSubscription();
      } catch (_) {}
      this._playbackBufferingSubscription = null;
    }
    this._playbackStateListeners.clear();
    this._playbackState = 'stopped';
    this._initialized = false;
    if (this._deckSubscription) {
      try {
        this._deckSubscription();
      } catch (_) {}
      this._deckSubscription = null;
    }
    if (this._syncBpmBridgeSub) {
      try { this._syncBpmBridgeSub(); } catch (_) {}
      this._syncBpmBridgeSub = null;
    }
    // The deck itself outlives the engine (it belongs to the VOICE — the session disposes it at
    // voice teardown); only this engine's position feed is released here.
    this.deck.setPositionSource(null);
    setPlaybackState('stopped');
    this.mediaSession?.dispose?.();
    try {
      this.transport.stop?.();
    } catch (_) {}

    try {
      this.playback.triggerStop?.();
    } catch (_) {}
    try {
      this.playback.dispose?.();
    } catch (_) {}

    this._dimensionChains.forEach((chain) => {
      Object.values(chain.axisRacks || {}).forEach((rack) => rack?.dispose?.());
      try { chain.inputGain?.disconnect?.(); } catch (_) {}
      try { chain.outputGain?.disconnect?.(); } catch (_) {}
      chain.inputGain?.dispose?.();
      chain.outputGain?.dispose?.();
    });
    this._dimensionChains.clear();
    this._dimensionOrder = [];
    // Racks have already notified `(slot, false)` for every live slot above.
    this._effectSlotObservers.clear();

    // Level taps first: they are branches OFF the gain stages, so they must come away before the
    // nodes they read are disposed.
    this._disposeLevelTaps();

    // Disconnect and dispose normalization gain
    try { this.normalizationGain?.disconnect?.(); } catch (_) {}
    this.normalizationGain?.dispose?.();
    this.normalizationGain = null;

    try { this.inputLevelGain?.disconnect?.(); } catch (_) {}
    this.inputLevelGain?.dispose?.();
    this.inputLevelGain = null;
    this._inputStageTarget = null;

    this.bodyLevelGain?.disconnect?.();
    this.bodyLevelGain?.dispose?.();
    this.bodyLevelGain = null;

    // Disconnect all of masterGain's outgoing edges — mode-agnostic: single-orbiter feeds the limiter,
    // multi-orbiter feeds the meter + the shared master bus. Never connected to a shared node
    // that outlives this voice, so disconnect-all is safe.
    try { this.masterGain?.disconnect?.(); } catch (_) {}
    this.masterGain?.dispose?.();
    this.masterGain = null;

    // Disconnect and dispose the per-voice limiter (null in multi-orbiter mode — the shared host owns it).
    try { this.limiter?.disconnect?.(this.masterMeter); } catch (_) {}
    try { this.limiter?.disconnect?.(this._outputNode ?? Tone.getDestination()); } catch (_) {}
    this.limiter?.dispose?.();
    this.limiter = null;

    this.masterMeter?.dispose?.();
    this.masterMeter = null;
  }

  /**
   * The start path's position step. Normally: align to the shared phase. But when the user CHOSE this
   * start position, the spot wins — the alignment would project from the phase the deck had before
   * and quietly overwrite it, which in a room moved both clients off the position they had just been
   * given. The phase is then re-stated FROM that spot instead, so the corrections that follow measure
   * against where playback actually opened rather than pulling it back off the mark.
   */
  async _settleStartPosition({ atSharedBeat = null } = {}) {
    // A chosen start position becomes the PHASE first — "at the beat this start belongs to, this voice
    // is HERE" — and the alignment then projects from THAT anchor instead of the one the deck was
    // carrying before. On time it lands exactly on the spot; processed late, it opens where the room
    // has already got to, which is the catch-up an ordinary start gets. Skipping the alignment instead
    // pinned a late client behind everyone else; leaving the old anchor in place moved every client
    // off the spot it had just been given.
    if (this._startPositionIsExplicit()) this._anchorPhaseAtCurrentPosition(atSharedBeat);
    await this._alignWrapPlaybackPosition({ force: true });
  }

  /**
   * Declare "at the beat happening now, this voice is HERE" — the same anchor a seek establishes,
   * from the position the playhead is already on. No joined shared clock means no phase to state.
   */
  _anchorPhaseAtCurrentPosition(atSharedBeat = null) {
    if (this._sharedClockBeat() === null) return;
    // The SHARED beat this start was scheduled on when there is one: two browsers whose timers fire a
    // few milliseconds apart would otherwise record different anchors for the same start, and drift
    // correction cannot close that — each client believes its own. `-1` on the live read is the same
    // 1-based convention `_seekNow` anchors on; a scheduled beat is already absolute.
    const beat = Number.isFinite(atSharedBeat)
      ? Number(atSharedBeat)
      : Number(this.deck?.getCurrentSyncBeat?.()) - 1;
    // The position ASKED for, in raw source time — never the live playhead, which carries this device's
    // output-latency lead (and the deck scales that lead by its follow ratio, so it cannot be undone
    // by subtraction here). Anchoring the led figure would have the lead applied twice by the next
    // projection and walk the playhead further off the spot with every correction.
    const sourceMs = Number(this._startPosition?.rawMs);
    if (!Number.isFinite(beat) || !Number.isFinite(sourceMs)) return;
    this.deck.setSeekAnchor?.(beat, sourceMs);
  }

  async _alignWrapPlaybackPosition({ force = false } = {}) {
    // Gated on the JOINED SHARED CLOCK, not on the sync flag. The aligned position is derived from
    // the session beat, and with no session joined that beat is a free-running local count — aligning
    // to it would fling the playhead to a spot nobody shares. No joined clock = nothing to align to;
    // the own-grid snap owns that case instead (the two are mutually exclusive by this predicate).
    if (this._sharedClockBeat() === null) return;
    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    const currentPositionMs = Number(this.playback?.getCurrentPositionMs?.() ?? NaN);
    // The regime the SHARED phase is in — resolved from the anchor, not from this client's playhead,
    // or two clients aligning to the same beat would fold it through different loops and diverge.
    const loopRange = this._phaseLoopRange();
    // Lead the aligned playhead by the per-device manual audio offset so THIS device's audio
    // comes out that many ms earlier (compensating output latency the browser can't measure — see
    // config/audioOffset.js). Applied here, at the one owner of the beat→playhead mapping, it survives
    // the re-alignment at fire time and every periodic re-align, and covers seek too — so the device's
    // whole output is uniformly shifted. Positive offset = play slightly ahead of the grid = earlier
    // acoustically. Offset 0 (default) leaves the aligned position byte-identical.
    const outputLeadMs = getManualAudioOffsetMs();
    const targetMs = this.deck.computeAlignedSourcePositionMs({ durationMs, loopRange, outputLeadMs });
    if (!Number.isFinite(targetMs)) return;
    const currentMs = Number.isFinite(currentPositionMs) ? currentPositionMs : 0;
    if (!force && Number.isFinite(currentMs) && Math.abs(currentMs - targetMs) < 12) {
      return;
    }
    // A forced re-alignment restates the phase from scratch — anything the ladder had accumulated
    // against the old reading is void. (The ANCHOR is deliberately untouched: this aligns THROUGH
    // it. A late joiner is handed the room's phase and then starts, so clearing it here would wipe
    // exactly what it was just told.)
    this._clearDriftCorrection();
    await this.playback?.setPosition?.(targetMs);
    this._resolveLoopEngagement(targetMs); // the playhead moved — the loop is re-decided from where it landed
    this.mediaSession?.syncPositionState?.();
  }

  _syncWrapPlaybackRate({ immediate = false } = {}) {
    // Only owns the rate for voices with NO speed-control effect at all. A tempoPitch effect (whether its
    // active module is a tempo OR a pitch module) is the factory's to own — gate on the WIDER speed-target
    // set, not getTempoManagedTargets() (tempo* modules only), or a voice on a pitch module gets its rate
    // double-written here and in the factory.
    if (this.#getSpeedControlTargets().length > 0) {
      return;
    }
    // On mobile the speed lock pins audio to its native rate. Don't compute or push a
    // transport warp ratio the sink would only reject — the engine stays the authoritative gate,
    // this early-out just avoids the needless work on constrained devices.
    if (this._speedControlDisabled) {
      return;
    }
    // WARP: a voice with no tempoPitch effect time-stretches to its deck's transport tempo. The deck
    // owns the whole rule — followRatio is transport/native while following (warp on; synced OR an
    // unsynced collection deck on its own tempo; solo additionally requires the session enabled,
    // byte-identical to the historical single-orbiter behavior) and exactly 1 otherwise.
    const baseRate = Math.max(0.01, Number(this.deck.followRatio) || 1);
    // DRIFT TRIM: the gentlest rung of the shared-timecode correction, composed here rather than
    // written separately — this method is the one owner of the adapter's playback rate, and a second
    // writer would fight it on every bpm/status re-fan. Zero (the default) leaves the rate untouched.
    const trim = Number.isFinite(this._driftTrim) ? this._driftTrim : 0;
    const targetRate = Math.max(0.01, baseRate * (1 + trim));
    if (typeof this.playback?.setPlaybackRate === 'function') {
      this.playback.setPlaybackRate(targetRate, { immediate });
      this.mediaSession?.syncPositionState?.();
    }
  }

  // ── shared timecode: drift measurement + correction ────────────────────────

  /**
   * The loop the user DREW and left on — the whole logical loop, running or merely held. This is
   * what a snapshot publishes and what the markers draw.
   * @returns {{start: number, end: number}|null}
   */
  _enabledLoopRange() {
    if (this._loopEnabled !== true) return null;
    const range = this._loop;
    if (!range || !Number.isFinite(range.start) || !Number.isFinite(range.end)) return null;
    return range.end > range.start ? range : null;
  }

  /**
   * Mirror the RANGE onto the playback sink. A sink that does not have it yet — a fresh backend after
   * a swap, a reload — is given it here, which is why a swap needs no rescue of its own: the loop
   * never lived there to be lost. Whatever the sink rounds or clamps it to becomes the canonical
   * range, or the comparison would differ forever and rewrite the sink on every resolve.
   *
   * Always installed DISENGAGED: engagement is the resolver's to decide, from the range that lands.
   * @returns {boolean} whether the sink's range actually changed.
   */
  _installSinkLoopRange() {
    const range = this._loop;
    const sinkRange = typeof this.playback?.getLoopRange === 'function' ? this.playback.getLoopRange() : null;
    if (!range) {
      if (!sinkRange) return false;
      this.playback?.clearLoop?.();
      return true;
    }
    if (this._sameLoopRange(sinkRange, range)) return false;
    this.playback?.setLoopRange?.(range.start, range.end, { active: false });
    const applied = typeof this.playback?.getLoopRange === 'function' ? this.playback.getLoopRange() : null;
    if (Number.isFinite(applied?.start) && Number.isFinite(applied?.end) && applied.end > applied.start) {
      this._loop = { start: applied.start, end: applied.end };
    }
    return true;
  }

  /** Same loop range, to the sub-millisecond the sinks round to. */
  _sameLoopRange(a, b) {
    if (!a || !b) return a === b;
    return Math.abs(Number(a.start) - Number(b.start)) < 0.5
      && Math.abs(Number(a.end) - Number(b.end)) < 0.5;
  }

  /** The loop that governs the wrap AT `positionMs` — enabled and CONTAINING it, else null. Every
   *  "which loop applies here" question (a seek target, an own-grid snap, an anchor projection, the
   *  period drift folds against) asks THIS, so a position outside the loop is never folded back in.
   *  Inclusive of the end: a playhead exactly on it is in phase with the loop's start. */
  _loopRangeAt(positionMs) {
    const range = this._enabledLoopRange();
    if (!range || !Number.isFinite(positionMs)) return null;
    return positionMs >= range.start && positionMs <= range.end ? range : null;
  }

  /**
   * The loop regime the SHARED PHASE is expressed in: the loop containing the ANCHOR the projection
   * runs from, falling back to the playhead when this deck rides the bare grid.
   *
   * Deliberately not "where the local playhead happens to be": two clients converging on the same
   * snapshot would then project it through different regimes and land in different places, which is
   * the opposite of converging. Deliberately not "the enabled loop" either: when the room has
   * seeked OUT of a loop the anchor sits outside it, and folding the shared phase through the loop
   * anyway is exactly the wrap-back-in this whole model exists to stop.
   */
  _phaseLoopRange() {
    // The LOGICAL loop (shared state), and the deck decides whether the shared phase is expressed in
    // it — from the anchor, never from a playhead. Reading the local playhead here is what let two
    // clients on opposite sides of a loop edge project the same anchor through different periods and
    // sit a whole loop-length apart, each reading almost no drift because each was right in its own
    // regime. The deck's rule keeps a looping voice looping (the phase reaches the loop and folds into
    // it) and still lets a seek out of one stay out (the seek's anchor lands past the loop's end).
    return this.deck?.phaseLoopRange?.(this._enabledLoopRange()) ?? null;
  }

  /** Half-open containment `[start, end)` — the SAME interval the wrap implies (a position at the
   *  loop end has already wrapped to the start, so it is not "in" the loop at the end). */
  _loopContains(range, positionMs) {
    return Boolean(range)
      && Number.isFinite(positionMs)
      && positionMs >= range.start
      && positionMs < range.end;
  }

  /**
   * Should the SINK be running this loop from `positionMs`? Yes while the playhead is inside the
   * range — and yes while it is still travelling toward it, because both sinks wrap only when the
   * playhead reaches the far edge and neither ever pulls a playhead that has not got there yet. So a
   * loop armed ahead of the playhead plays into the range and starts looping exactly at its edge,
   * with the arrival owned by the audio clock rather than predicted by a timer that a stalled main
   * thread, a rate change or a direction change could make wrong.
   *
   * Past the range is the one case that must NOT engage: that is precisely the wrap-back-in a seek
   * out of a loop has to escape.
   */
  _loopEngagesAt(range, positionMs) {
    if (!range || !Number.isFinite(positionMs)) return false;
    return this.playback?.isPlaybackReverse?.() === true
      ? positionMs >= range.start
      : positionMs <= range.end;
  }

  /**
   * THE loop rule, and the only writer of loop engagement.
   *
   * A loop is `(range, enabled)` and is only ever drawn by a user. It RUNS from wherever the playhead
   * can still reach it, and starts looping when the playhead arrives at its edge. A playhead already
   * PAST the range leaves the loop HELD — the range stays stored, so the in/out markers stay drawn
   * and a snapshot still carries it — and it runs again as soon as a seek brings the playhead back.
   *
   * Engagement is never an input: user edits, remote applies, snapshot restores, seeks and play all
   * land here, which is why a listener applying the holder's loop can no longer be yanked into a
   * range the holder has just seeked out of.
   *
   * @param {number|null} positionMs Where the playhead is (or is about to land, for a seek).
   *   Omit to read it live.
   * @returns {boolean} whether the loop is engaged after this call.
   */
  _resolveLoopEngagement(positionMs = null) {
    // Read the engagement that stands BEFORE any mirror write: installing a range resets the sink's
    // own flag, and both the unreadable-playhead case and the transport clear below need the real
    // prior state to compare against.
    const wasEngaged = typeof this.playback?.isLooping === 'function'
      ? Boolean(this.playback.isLooping())
      : false;
    // The RANGE goes in first, because the sink is allowed to round or clamp it (a minimum loop
    // length, the end of the decoded buffer) and engagement has to be judged against the range that
    // actually exists — a range the sink widened until it reached the playhead must not stay held.
    const rangeChanged = this._installSinkLoopRange();

    const range = this._enabledLoopRange();
    const pos = Number.isFinite(positionMs)
      ? positionMs
      : Number(this.playback?.getCurrentPositionMs?.() ?? NaN);
    // With no loop to run there is nothing to judge — off is off, whatever the playhead reads. With
    // one, an unreadable playhead is no reason to drop a loop that is ALREADY running — but only the
    // same loop keeps its engagement that way: engaging a range that just arrived, with no idea where
    // the playhead is, is how it would be folded into a loop it may be well past. A new loop simply
    // waits, visible and on, until the next resolve can see the playhead. Either way the transport
    // below still gets reconciled, or it would be left wrapping through a range that is gone.
    const engaged = (range && !Number.isFinite(pos))
      ? (wasEngaged && !rangeChanged)
      : this._loopEngagesAt(range, pos);

    if (engaged !== (this.playback?.isLooping?.() === true)) {
      this.playback?.setLoopEnabled?.(engaged);
    }
    // The period the drift folds against changed, so the samples collected under the old one are void
    // and an armed re-align is measuring the wrong thing. The projection TOKEN deliberately does NOT
    // move: a correction seek releases the loop itself on its way out of it, and invalidating the
    // token here would abort the very seek that asked for the release. Only a change to the loop
    // ITSELF — its range or its on/off — moves that token, and its own setters do it.
    if (engaged !== wasEngaged) {
      this._resetDriftWindow();
      this._cancelPendingDriftRealign();
    }
    // The transport mirrors ENGAGEMENT only; it has no held state of its own. Compared against what a
    // mirrored range actually BECOMES there — the transport holds nothing shorter than its own floor,
    // so a very short loop comes back longer than it went in. Asking for an exact match would rewrite
    // it on every resolve forever; accepting merely "no shorter" would leave it on a stale longer
    // range after the loop was tightened.
    if (engaged && this._loop) {
      const expectedEndMs = Math.max(this._loop.end, this._loop.start + TRANSPORT_MIN_LOOP_MS);
      const mirrored = Boolean(this.transport?.isLooping)
        && Math.abs((Number(this.transport.loopStartSeconds) * 1000) - this._loop.start) < 0.5
        && Math.abs((Number(this.transport.loopEndSeconds) * 1000) - expectedEndMs) < 0.5;
      if (!mirrored) this.transport?.setLoopRange?.(this._loop.start, this._loop.end);
    } else if (wasEngaged || this.transport?.isLooping) {
      this.transport?.clearLoop?.();
    }
    return engaged;
  }

  /** The wrap period drift folds against: the engaged loop's length, else the track duration. */
  _driftPeriodMs(loopRange) {
    if (loopRange) return loopRange.end - loopRange.start;
    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    return Number.isFinite(durationMs) && durationMs > 0 ? durationMs : null;
  }

  /**
   * A loop edit changed the wrap period. Bump the token an armed correction re-checks AND drop the
   * sample window: a median taken half over the whole track and half over a loop mixes two different
   * fold periods, which reads as a large drift nobody has.
   */
  _bumpLoopGeneration() {
    this._loopGeneration += 1;
    this._resetDriftWindow();
    this._cancelPendingDriftRealign();
    // `_loopGeneration` above is half of `_projectionToken()`, which is what an in-flight correction
    // re-checks — so a loop edit invalidates a correction whose timer has ALREADY fired and has no
    // pending slot left to cancel. Deliberately NOT the seek epoch: that would also kill an ordinary
    // in-flight seek, including a routed room seek a remote loop edit happens to overlap.
  }

  /**
   * Every input a correction's target is computed from: the deck's projection generation (anchor,
   * native tempo, grid marker, track) and this voice's loop topology. A correction re-checks it both
   * when its timer fires AND while its position write is in flight — any of these moving means the
   * number it computed describes a timeline that no longer exists.
   */
  _projectionToken() {
    return `${this.deck?.projectionGeneration ?? 0}:${this._loopGeneration}`;
  }

  /**
   * Is a drift sample meaningful — and therefore correctable — right now?
   *
   * Reverse playback and any speed-control effect are excluded OUTRIGHT, not just from the trim
   * rung: the expected-position math advances forward at the deck's ratio, so against a reversed or
   * effect-driven playhead a hard re-align would be actively destructive, not merely useless.
   */
  _driftSamplingActive() {
    if (this.playback?.isPlaying?.() !== true) return false;
    if (this._sharedClockBeat() === null) return false;
    if (this.deck?.following !== true) return false;
    if (this._playbackState === 'buffering' || this._bufferingState?.isBuffering === true) return false;
    if (this.isPlaybackReverse?.() === true) return false;
    if (this.#getSpeedControlTargets().length > 0) return false;
    return true;
  }

  /** Drop the sample window (never median-fold across a topology change, a stall, or a correction). */
  _resetDriftWindow(nowMs = null) {
    this._driftSamples = [];
    this._driftOverCount = 0;
    this._driftMedianMs = null;
    this._driftLastPositionMs = null;
    this._driftLastSampleAt = Number.isFinite(nowMs) ? nowMs : this._monotonicNowMs();
  }

  /** Wall time since the previous observation — 0 when there is no previous one to have a gap from. */
  _driftElapsedMs(nowMs) {
    const previousAt = this._driftLastSampleAt;
    return Number.isFinite(previousAt) ? nowMs - previousAt : 0;
  }

  /** Zero the trim + window + any armed re-align WITHOUT re-fanning the rate — for callers that are
   *  about to write the rate themselves. Standalone callers use `_clearDriftCorrection`. */
  _resetDriftCorrection() {
    this._driftTrim = 0;
    this._cancelPendingDriftRealign();
    this._resetDriftWindow();
  }

  /** Same, but re-fans the rate so a trim can never stay baked into what is actually playing. */
  _clearDriftCorrection() {
    // Re-fan only when a trim was actually live: with none, the composed target equals the baseline
    // the last re-fan already wrote, so the write would be pure churn on every forced alignment.
    const hadTrim = Number.isFinite(this._driftTrim) && this._driftTrim !== 0;
    this._resetDriftCorrection();
    if (hadTrim) this._syncWrapPlaybackRate({ immediate: false });
  }

  /** Public kill-switch entry point (debug seam) — engaging correction-off must leave no residue. */
  clearDriftCorrection() {
    this._clearDriftCorrection();
  }

  /** The per-voice drift readout. The debug seam holds NO state of its own — it reads this. */
  getDriftState() {
    // The preconditions ship WITH the numbers: "no drift reading" and "drift is zero" look identical
    // otherwise, and the usual answer is that one of these is false — most often a voice whose own
    // sync is off, or a pulse that never joined.
    return {
      lastMs: this._driftLastSampleMs,
      medianMs: this._driftMedianMs,
      trim: this._driftTrim,
      sampling: Boolean(this._driftTimer),
      parked: Boolean(this._driftTimer) && this._sharedClockBeat() === null,
      playing: this.playback?.isPlaying?.() === true,
      syncEnabled: this.deck?.getStatusDetail?.()?.enabled === true,
      clockJoined: this._sharedClockBeat() !== null,
      following: this.deck?.following === true,
      // `following` is false for two very different reasons — warp switched off, or the track having
      // no analyzed BPM at all (which locks warp off and leaves NO grid to align to). Both, so the
      // next reading says which.
      warp: this.deck?.warp === true,
      nativeTempo: this.deck?.nativeTempo ?? null,
      roomRouted: Boolean(this._roomTransport),
      anchor: this.deck?.seekAnchor ?? null,
    };
  }

  _monotonicNowMs() {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  /**
   * Is there a shared phase for this voice to hold at all? Gated on the deck's OWN sync flag rather
   * than on a joined pulse, because that flag is a cheap field read that changes through an event we
   * already listen to — a sync-enabled deck whose pulse has not joined yet ticks cheaply for a
   * moment, while a solo or deliberately unsynced voice never arms a timer in the first place.
   */
  _driftSamplerEligible() {
    if (this._playbackState !== 'playing') return false;
    if (this.deck?.getStatusDetail?.()?.enabled !== true) return false;
    // …and only where a shared clock can actually EXIST. This mirrors the pulse's own joined rule
    // (a network peer, or ≥2 synced voices in this tab): a lone synced orbiter has nobody to lock
    // with, so arming a timer to keep discovering that is exactly the continuous background work
    // this app does not do.
    return Boolean(this._roomTransport) || syncEnabledDeckCount() >= 2;
  }

  /** The ONE place the sampler is armed or disarmed, from playback state and sync eligibility. */
  _refreshDriftSampler() {
    if (this._driftSamplerEligible()) this._startDriftSampler();
    else this._stopDriftSampler();
  }

  _startDriftSampler() {
    if (this._driftTimer || typeof setTimeout !== 'function') return;
    this._resetDriftWindow();
    this._scheduleNextDriftSample();
  }

  /**
   * Self-scheduling rather than a fixed interval, so the cost matches what there is to measure: a
   * voice that is eligible on paper but has no JOINED clock yet (or never gets one — offline, relay
   * down) parks at a slow tick instead of waking twice a second to discover it has nothing to do.
   * It heals itself the moment the clock joins, which no deck event reliably announces.
   */
  _scheduleNextDriftSample() {
    if (typeof setTimeout !== 'function') return;
    const delayMs = this._sharedClockBeat() === null
      ? DRIFT_SAMPLE_PARKED_INTERVAL_MS
      : DRIFT_SAMPLE_INTERVAL_MS;
    // Remembered so the resume-gap check below can compare elapsed time against what was actually
    // ASKED for. Measuring it against the fast period would make every un-park look like a
    // suspension and fire an audible hard seek on a voice with no drift at all.
    this._driftScheduledDelayMs = delayMs;
    this._driftTimer = setTimeout(() => {
      this._driftTimer = null;
      this._sampleDrift();
      // Re-arm only while still wanted — `_stopDriftSampler` nulls the timer, and a sample that
      // stopped playback must not resurrect the loop.
      if (this._driftSamplerEligible()) this._scheduleNextDriftSample();
    }, delayMs);
  }

  _stopDriftSampler() {
    if (this._driftTimer) {
      try { clearTimeout(this._driftTimer); } catch (_) {}
      this._driftTimer = null;
    }
    this._driftLastSampleMs = null;
    this._clearDriftCorrection();
  }

  /**
   * One drift observation, and the ladder decision it may earn.
   *
   * Timer callbacks are OBSERVATIONS, not scheduling truth: a throttled or suspended tab delivers
   * them late, so the elapsed gap is checked first and the shared beat is re-read here rather than
   * trusted from when the interval was armed.
   */
  _sampleDrift() {
    const nowMs = this._monotonicNowMs();
    const elapsed = this._driftElapsedMs(nowMs);
    this._driftLastSampleAt = nowMs;

    if (!this._driftSamplingActive()) {
      this._resetDriftWindow(nowMs);
      return;
    }

    // A gap is time we did NOT ask for — the tab was suspended, or the device stalled. Measured
    // against the delay actually scheduled, so a deliberate parked tick is never mistaken for one.
    const scheduledMs = Number.isFinite(this._driftScheduledDelayMs)
      ? this._driftScheduledDelayMs
      : DRIFT_SAMPLE_INTERVAL_MS;
    // Rather than make a returning tab wait out two medians and a cooldown, converge it immediately —
    // the event-driven complement to the ladder.
    if (elapsed > scheduledMs * 6) {
      this._resetDriftWindow(nowMs);
      void this.realignToSharedPhase();
      return;
    }
    if (elapsed > scheduledMs * 3) {
      this._resetDriftWindow(nowMs);
      return;
    }

    // The regime the SHARED phase is in — the very one a correction would converge to, so the fold
    // period, the expectation and the containment test below cannot disagree with the target. Read
    // from the local playhead instead, they could: a client whose playhead had left the loop measured
    // itself against the whole track, and its correction then landed outside the loop for good.
    const loopRange = this._phaseLoopRange();
    const periodMs = this._driftPeriodMs(loopRange);
    // No fold period (duration unresolved, no loop around the phase) → no honest threshold to set.
    if (!(periodMs > 0)) {
      this._resetDriftWindow(nowMs);
      return;
    }

    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    // Expected and actual are compared LED-vs-LED: the playhead was placed at a led position, so the
    // device lead cancels. Comparing a raw expectation to a led actual would read the offset as drift.
    const expectedMs = this.deck.computeAlignedSourcePositionMs({
      durationMs,
      loopRange,
      outputLeadMs: getManualAudioOffsetMs(),
    });
    const actualMs = Number(this.playback?.getCurrentPositionMs?.());
    if (!Number.isFinite(expectedMs) || !Number.isFinite(actualMs)) {
      this._resetDriftWindow(nowMs);
      return;
    }

    // A playhead that has not moved since the last sample is stalled, not drifting — measuring it
    // would report the stall as an ever-growing error and then seek a stream that cannot play.
    const previousPositionMs = this._driftLastPositionMs;
    this._driftLastPositionMs = actualMs;
    if (Number.isFinite(previousPositionMs) && Math.abs(actualMs - previousPositionMs) < 1) {
      this._resetDriftWindow(nowMs);
      return;
    }

    if (loopRange && !this._loopContains(loopRange, actualMs)) {
      // The shared phase is in the loop and this playhead is not. Travelling TOWARD it is ordinary —
      // a loop drawn ahead of the playhead plays into it — and the two positions are simply on
      // different laps until it arrives, so skip the sample and keep the window.
      if (this._loopEngagesAt(loopRange, actualMs)) return;
      // PAST the loop's end it can never arrive: this playhead has escaped a loop the room is still
      // playing, and folding a position outside the loop into the loop's period would report some
      // arbitrary phase rather than the truth. Converge on the shared phase instead of measuring —
      // the target is computed in the loop regime, so it lands back inside and starts looping again.
      // The realign's own bar boundary and cooldown keep this from becoming a seek storm.
      this._resetDriftWindow(nowMs);
      // The kill switch gates automatic MOVEMENT, so it gates this too — and arming anyway would
      // re-arm on every sample, since a correction that exits at fire time records no cooldown.
      if (!isDriftCorrectionDisabled()) this._scheduleDriftRealign(nowMs);
      return;
    }

    const sample = foldDriftMs(actualMs - expectedMs, periodMs);
    if (sample == null) return;
    this._driftLastSampleMs = sample;
    this._driftSamples.push(sample);
    if (this._driftSamples.length > DRIFT_SAMPLE_WINDOW) this._driftSamples.shift();
    if (this._driftSamples.length < DRIFT_SAMPLE_WINDOW) return;

    const medianMs = medianOf(this._driftSamples);
    this._driftMedianMs = medianMs;
    if (isDebugSyncLoggingEnabled()) {
      try {
        console.debug('[drift]', { voice: this.deck?.voiceId ?? null, sample, medianMs, trim: this._driftTrim });
      } catch (_) {}
    }
    // The kill switch stops CORRECTION only — the number stays observable.
    if (isDriftCorrectionDisabled()) return;

    const { rung, trim } = decideDriftAction({ medianMs, periodMs, trim: this._driftTrim });
    if (rung === 'realign') {
      this._driftOverCount += 1;
      if (this._driftOverCount >= DRIFT_REALIGN_CONSECUTIVE) this._scheduleDriftRealign(nowMs);
      return;
    }
    this._driftOverCount = 0;
    if (trim !== this._driftTrim) {
      this._driftTrim = trim;
      this._syncWrapPlaybackRate({ immediate: false });
    }
  }

  /** Arm a hard re-align on the next shared bar — corrections land on bar lines, never mid-phrase. */
  _scheduleDriftRealign(nowMs, retries = 0) {
    if (this._pendingDriftRealign) return;
    // A retry continues a correction that already cleared the cooldown — re-testing it here would
    // silently drop the re-arm the boundary check just asked for.
    if (retries === 0 && nowMs - this._driftLastRealignAt < DRIFT_REALIGN_COOLDOWN_MS) return;
    const targetBeat = this._nextSharedBarBeat();
    if (targetBeat === null) return;
    const delayMs = this._delayMsToSharedBeat(targetBeat);
    if (!Number.isFinite(delayMs)) return;
    const pending = { timer: null, canceled: false, targetBeat, token: this._projectionToken(), retries };
    this._pendingDriftRealign = pending;
    pending.timer = setTimeout(() => {
      pending.timer = null;
      void this._fireDriftRealign(pending);
    }, Math.max(0, delayMs));
  }

  // Deliberately does NOT bump the seek epoch: `_seekNow` clears the correction as part of its own
  // run, so bumping here would make every seek supersede itself. An in-flight re-align is stopped
  // through `_cancelPendingQuantizedSeek` instead — pause, stop, dispose and any newer seek all
  // call it, which is exactly the set that should be able to stop one.
  _cancelPendingDriftRealign() {
    const pending = this._pendingDriftRealign;
    if (!pending) return;
    pending.canceled = true;
    if (pending.timer) {
      try { clearTimeout(pending.timer); } catch (_) {}
      pending.timer = null;
    }
    this._pendingDriftRealign = null;
  }

  /**
   * The armed bar arrived — but everything it assumed is re-checked before a note moves: still
   * joined and playing, correction still enabled, and the projection it was armed against still the
   * one in force. A tempo change under the one-shot timer moves the boundary itself; rather than
   * re-align off-grid, recompute and take the next bar.
   */
  async _fireDriftRealign(pending) {
    if (pending.canceled || this._pendingDriftRealign !== pending) return;
    this._pendingDriftRealign = null;
    if (!this._driftSamplingActive() || isDriftCorrectionDisabled()) return;
    if (pending.token !== this._projectionToken()) return;

    const REALIGN_BOUNDARY_SLACK_MS = 40;
    const MAX_REALIGN_RETRIES = 2;
    const delayMs = this._delayMsToSharedBeat(pending.targetBeat);
    if (Number.isFinite(delayMs)
      && Math.abs(delayMs) > REALIGN_BOUNDARY_SLACK_MS
      && pending.retries < MAX_REALIGN_RETRIES) {
      this._scheduleDriftRealign(this._monotonicNowMs(), pending.retries + 1);
      return;
    }

    this._driftLastRealignAt = this._monotonicNowMs();
    await this.realignToSharedPhase();
  }

  /**
   * Put this voice back on the phase the shared beat implies, through the deck anchor, applying it
   * to the transport AND the playback clock — the way a seek does. (`_alignWrapPlaybackPosition`
   * writes the playback position only, which is fine at a start where the transport is about to be
   * started anyway, but mid-playback it would leave the secondary clock behind.)
   *
   * Also the convergence path for a late joiner that is already playing and for a tab returning from
   * suspension: both need the phase NOW, not after two medians and a cooldown.
   */
  async realignToSharedPhase({ correction = true } = {}) {
    // The kill switch turns automatic CORRECTION off (measurement stays on), and a drift-driven
    // re-align is the loudest correction there is — checked at entry and again in flight, since it
    // can be thrown while a position write is already pending. It does NOT gate `correction: false`
    // callers: those are applying authoritative ROOM state, and a debug flag must never be able to
    // leave one client on a phase the room has moved off.
    if (!this._driftSamplingActive()) return;
    if (correction && isDriftCorrectionDisabled()) return;
    // Same regime for everyone converging on this phase — see `_phaseLoopRange`.
    const loopRange = this._phaseLoopRange();
    const durationMs = Number(this.getDurationMs?.() ?? this.playback?.getDurationMs?.() ?? 0);
    // RAW (un-led): `_seekNow` applies this device's lead itself — leading here too would double it.
    const targetMs = this.deck.computeAlignedSourcePositionMs({ durationMs, loopRange, outputLeadMs: 0 });
    if (!Number.isFinite(targetMs)) return;
    // A correction is only valid against the topology it was computed for: a loop edit landing
    // mid-flight invalidates the target, and unlike an armed timer there is no pending slot to cancel.
    // A correction stays valid only while BOTH still hold: the projection it was computed from, and
    // the very condition that authorized it. Sync going off (or the shared clock dropping) mid-flight
    // clears the drift state but moves neither epoch — without this, an obsolete shared-phase
    // correction would land on a voice that is no longer following that phase at all.
    const projection = this._projectionToken();
    await this._seekNow(targetMs, {
      isValid: () => this._projectionToken() === projection
        && this._driftSamplingActive()
        && !(correction && isDriftCorrectionDisabled()),
    });
  }

  _handleBodyLevelParameter(value) {
    const rawDb = typeof value === 'number' ? value : this.userManager?.getRawValue?.(PREMIX_PARAM) ?? 0;
    this._rampGainDb(this.bodyLevelGain ?? this.masterGain, rawDb);
  }

  _handleInputLevelParameter(value) {
    const rawDb = typeof value === 'number' ? value : this.userManager?.getRawValue?.(INPUT_GAIN_PARAM) ?? 0;
    this._rampGainDb(this.inputLevelGain, rawDb);
  }

  /**
   * Move a user gain stage to `rawDb` over a short ramp. Never a raw `.value =` write while
   * audio runs: a step change on a live signal is an audible click, so the whole cascade below
   * exists to find the smoothest write this AudioParam implementation supports.
   */
  _rampGainDb(targetGain, rawDb) {
    if (!Tone || !targetGain) return;
    const dbValue = Math.max(GAIN_MIN_DB, Math.min(GAIN_MAX_DB, rawDb));
    // The bottom of the fader means OFF. dbToGain(-60) is ~0.001 — inaudible, but not silence, so
    // a fader pulled all the way down would still leak signal into the chain. Snap it to true zero.
    const gain =
      dbValue <= GAIN_MIN_DB
        ? 0
        : (Tone.dbToGain ? Tone.dbToGain(dbValue) : Math.pow(10, dbValue / 20));
    const now = typeof Tone.now === 'function' ? Tone.now() : 0;

    if (typeof targetGain.gain?.rampTo === 'function') {
      targetGain.gain.rampTo(gain, 0.05);
    } else if (typeof targetGain.gain?.setTargetAtTime === 'function') {
      const audioContext = Tone.getContext?.()?.rawContext ?? Tone.context?.rawContext ?? null;
      const time = audioContext?.currentTime ?? now;
      targetGain.gain.setTargetAtTime(gain, time, 0.05);
    } else if (typeof targetGain.gain?.linearRampToValueAtTime === 'function') {
      const audioContext = Tone.getContext?.()?.rawContext ?? Tone.context?.rawContext ?? null;
      const time = audioContext?.currentTime ?? now;
      try { targetGain.gain.setValueAtTime(targetGain.gain.value, time); } catch (_) {}
      targetGain.gain.linearRampToValueAtTime(gain, time + 0.05);
    } else {
      targetGain.gain.value = gain;
    }
  }

  /**
   * Extracts playbackGainDb from track metadata
   * @returns {number} Gain in dB (0 if not available)
   */
  _extractNormalizationGainDb() {
    const metadata = this.playbackTrackData?.metadata || {};
    const normalization = metadata?.audioNormalization;

    if (!normalization) return 0;

    const playbackGainDb = Number(normalization.playbackGainDb);

    if (!Number.isFinite(playbackGainDb)) return 0;

    // Optional: Safety check against maxAllowableGainDb
    const maxGainDb = Number(normalization.maxAllowableGainDb);
    if (Number.isFinite(maxGainDb) && playbackGainDb > maxGainDb) {
      console.warn(
        `[AudioEngine] Clamping normalization gain ${playbackGainDb.toFixed(2)} dB to max ${maxGainDb.toFixed(2)} dB`
      );
      return maxGainDb;
    }

    return playbackGainDb;
  }

  /**
   * Applies normalization gain from track metadata
   * Called once during engine initialization
   */
  _applyNormalizationGain() {
    if (!this.normalizationGain) return;

    const gainDb = this._extractNormalizationGainDb();
    // Use Tone.dbToGain if available (more accurate), fallback to manual conversion
    const gainLinear = Tone.dbToGain ? Tone.dbToGain(gainDb) : Math.pow(10, gainDb / 20);

    this.normalizationGain.gain.value = gainLinear;

    if (gainDb !== 0) {
      debugEngine(`applied normalization gain: ${gainDb.toFixed(2)} dB (${gainLinear.toFixed(3)}x)`);
    }
  }

  _ensureDimensionChain(dimensionId, effectsConfig = null, axesToRefresh = AXES) {
    if (!dimensionId) return null;

    const existing = this._dimensionChains.get(dimensionId);
    const config = this._ensureEffectsConfigDimension(
      effectsConfig || this._getCachedEffectsConfig(dimensionId) || this._cloneEffectsConfig(),
      dimensionId,
    );

    if (existing) {
      const refreshSet = new Set(
        Array.isArray(axesToRefresh) && axesToRefresh.length ? axesToRefresh : AXES,
      );
      if (!refreshSet.size) {
        return existing;
      }
      AXES.forEach((axis) => {
        if (!refreshSet.has(axis)) return;
        const rack = existing.axisRacks[axis];
        if (rack) {
          rack.setPerformanceProfile?.(this.performanceProfile);
          rack.configure(config[axis] || cloneRackConfig({ dimensionId }));
        }
      });
      return existing;
    }

    const ToneRef = Tone;
    // Per-stage input headroom: attenuate at the START of each dimension stage so
    // its processors have headroom and can't clip internally. 3 stages × STAGE_HEADROOM_DB.
    const inputGain = new ToneRef.Gain(dbToLinear(STAGE_HEADROOM_DB));
    const outputGain = new ToneRef.Gain(1);
    enforceStereo(inputGain);
    enforceStereo(outputGain);

  const axisRacks = {};
  let previousNode = inputGain;

    // Final deck channel (explicit stereo panner at end-of-chain for this dimension).
    // Unity gain: the old -6 dB here both over-attenuated (3 × -6 = -18 dB in
    // series) and gave processors no input headroom. Headroom now lives at each stage's
    // inputGain (STAGE_HEADROOM_DB); this node is purely the panner.
    const deckChannel = new ToneRef.Channel({ volume: 0, pan: 0 });
    // Force stereo semantics where supported to avoid accidental mono
    try {
      enforceStereo(deckChannel);
    } catch (_) {}

    this.axisOrder.forEach((axis) => {
      const rack = new EffectsRack({
        channel: axis,
        dimensionId,
        controllers: { playback: this, deckChannel },
        performanceProfile: this.performanceProfile,
        deck: this.deck,
      });
      rack.init({
        Tone: ToneRef,
        config: config[axis] || cloneRackConfig({ dimensionId }),
        performanceProfile: this.performanceProfile,
      });
      rack.observeSlots((slot, present) => this._notifyEffectSlot(slot, present));
      axisRacks[axis] = rack;

      const inputNode = rack.getInputNode();
      const outputNode = rack.getOutputNode();

      if (inputNode && typeof previousNode.connect === 'function') {
        previousNode.connect(inputNode);
      } else if (typeof previousNode.connect === 'function') {
        previousNode.connect(outputNode);
      }

      previousNode = outputNode;
    });

    // Ensure deck-level channel is last before output gain
    try {
      previousNode.connect(deckChannel);
      deckChannel.connect(outputGain);
    } catch (error) {
      console.warn('[AudioEngineAdapter] Failed to connect deck channel', error);
      try { previousNode.connect(outputGain); } catch (_) {}
    }

    const chain = {
      dimensionId,
      inputGain,
      outputGain,
      axisRacks,
      deckChannel,
    };

    // Optional targeted debug for X axis on EW::III
    try {
      if (typeof window !== 'undefined' && window.__DEBUG_AXIS_X_DIM3 && dimensionId === 'EW::III') {
        const xr = axisRacks.x;
        const configX = (effectsConfig || {})?.x;
        void xr; void configX;
      }
    } catch (_) {}

    this._dimensionChains.set(dimensionId, chain);
    this._dimensionIds.add(dimensionId);
    if (!this._dimensionOrder.includes(dimensionId)) {
      this._dimensionOrder.push(dimensionId);
    }
    this._registerDimensionParameters([dimensionId]);

    this._rewireGlobalChain();

    return chain;
  }

  _cloneEffectsConfig(effects = {}) {
    return {
      x: cloneRackConfig(effects.x),
      y: cloneRackConfig(effects.y),
      z: cloneRackConfig(effects.z),
    };
  }

  _ensureEffectsConfigDimension(config, dimensionId) {
    if (!config || !dimensionId) return config;
    const next = this._cloneEffectsConfig(config);
    const axisSummaries = [];
    AXES.forEach((axis) => {
      const axisConfig = next[axis] || cloneRackConfig({ dimensionId });
      const axisDimensionId = axisConfig.dimensionId ?? dimensionId;
      const axisDimensionLabel = axisConfig.dimensionLabel ?? axisDimensionId ?? dimensionId;
      const modules = Array.isArray(axisConfig.modules) ? axisConfig.modules : [];

      // Prepare module list: prefer modules scoped to this dimension, then fall back to global
      const prepared = modules.map((moduleConfig = {}) => {
        const scoped = cloneModuleConfig(moduleConfig, axisDimensionId);
        scoped.dimensionLabel = scoped.dimensionLabel ?? axisDimensionLabel;
        return scoped;
      });
      const specific = prepared.filter((m) => m.dimensionId === dimensionId);
      const global = prepared.filter((m) => !m.dimensionId);
      const filteredModules = [...specific, ...global].slice(0, MAX_MODULES);

      axisConfig.dimensionId = axisDimensionId;
      axisConfig.dimensionLabel = axisDimensionLabel;
      axisConfig.modules = filteredModules;
      next[axis] = axisConfig;
      axisSummaries.push({
        axis,
        dimensionId: axisDimensionId,
        moduleIds: filteredModules.map((module) => module?.effectId ?? null),
        populated: filteredModules.filter((module) => module?.effectId).length,
      });
    });
    debugEngineStacks('ensureEffectsConfigDimension', { dimensionId, axes: axisSummaries });
    return next;
  }

  _getCachedEffectsConfig(dimensionId) {
    if (!dimensionId) return null;
    const cached = this.effectsConfigByDimension.get(dimensionId);
    return cached ? this._cloneEffectsConfig(cached) : null;
  }

  _primeDimensionIdsFromConfig(effectsConfig = {}) {
    const ids = this._collectDimensionIdsFromEffectsConfig(effectsConfig);
    ids.forEach((id) => {
      if (id) {
        this._dimensionIds.add(id);
      }
    });
    if (ids.size) {
      debugEngineStacks('Prime dimension ids from effects config', {
        dimensionIds: [...ids],
      });
    }
  }

  _primeDimensionIdsFromStacks(stacks = null) {
    if (!stacks || typeof stacks !== 'object') return;
    const discovered = [];
    Object.entries(stacks).forEach(([stackId, stack]) => {
      if (!stack || typeof stack !== 'object') return;
      const dimensionIds = Object.keys(stack.dimensions || {}).filter(Boolean);
      dimensionIds.forEach((dimensionId) => this._dimensionIds.add(dimensionId));
      if (dimensionIds.length) {
        discovered.push({ stackId, dimensionIds });
      }
    });
    if (discovered.length) {
      debugEngineStacks('Prime dimension ids from stacks', { discovered });
    }
  }

  _collectDimensionIdsFromEffectsConfig(effectsConfig = {}) {
    const ids = new Set();
    AXES.forEach((axis) => {
      const axisConfig = effectsConfig[axis];
      if (!axisConfig) return;
      if (axisConfig.dimensionId) {
        ids.add(axisConfig.dimensionId);
      }
      const modules = Array.isArray(axisConfig.modules) ? axisConfig.modules : [];
      modules.forEach((module) => {
        if (module?.dimensionId) {
          ids.add(module.dimensionId);
        }
      });
    });
    return ids;
  }

  _getChangedAxes(previousConfig, nextConfig) {
    if (!previousConfig) return [...AXES];
    return AXES.filter((axis) =>
      this._hasAxisDefinitionChanged(previousConfig[axis], nextConfig[axis]),
    );
  }

  _hasAxisDefinitionChanged(prevAxisConfig, nextAxisConfig) {
    if (!prevAxisConfig && !nextAxisConfig) return false;
    if (!prevAxisConfig || !nextAxisConfig) return true;
    const prevSnapshot = this._buildAxisDefinitionSnapshot(prevAxisConfig);
    const nextSnapshot = this._buildAxisDefinitionSnapshot(nextAxisConfig);
    return JSON.stringify(prevSnapshot) !== JSON.stringify(nextSnapshot);
  }

  _buildAxisDefinitionSnapshot(axisConfig) {
    if (!axisConfig) return null;
    const modules = Array.isArray(axisConfig.modules) ? axisConfig.modules : [];
    // Consider an axis changed as soon as an effectId appears, even if moduleId
    // has not been selected yet. Racks can pick a default module on configure.
    const populatedModules = modules
      .filter((module) => module?.effectId)
      .map((module) => this._normalizeModuleDefinition(module));
    return {
      dimensionId: axisConfig.dimensionId ?? null,
      modules: populatedModules,
    };
  }

  _normalizeModuleDefinition(moduleConfig = {}) {
    return {
      effectId: moduleConfig.effectId ?? null,
      moduleId: moduleConfig.moduleId ?? null,
      inputParamId: moduleConfig.inputParamId ?? null,
      range: moduleConfig.range
        ? {
            min: Number.isFinite(moduleConfig.range.min) ? Number(moduleConfig.range.min) : null,
            max: Number.isFinite(moduleConfig.range.max) ? Number(moduleConfig.range.max) : null,
            equilibrium: Number.isFinite(moduleConfig.range.equilibrium ?? moduleConfig.range.init)
              ? Number(moduleConfig.range.equilibrium ?? moduleConfig.range.init)
              : null,
          }
        : null,
      settings: this._normalizePlainObject(moduleConfig.settings),
      mappings: sanitizeMappings(moduleConfig.mappings),
    };
  }

  _normalizePlainObject(value) {
    if (value === null || value === undefined) return undefined;
    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'object' ? this._normalizePlainObject(item) : item));
    }
    if (typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          const entry = value[key];
          acc[key] =
            entry && typeof entry === 'object'
              ? this._normalizePlainObject(entry)
              : entry;
          return acc;
        }, {});
    }
    return value;
  }

  _parseAxisParameterName(parameterName) {
    if (typeof parameterName !== 'string') return null;
    const match = parameterName.match(/^([xyz])(?::(.+))?$/i);
    if (!match) return null;
    return {
      axis: match[1].toLowerCase(),
      dimensionId: match[2] || null,
    };
  }

  _registerParameterSubscriptions() {
    if (this._subscriptionsRegistered) return;
    if (!this.userManager || typeof this.userManager.subscribe !== 'function') {
      console.warn('[AudioEngineAdapter] Cannot subscribe to parameters - missing userManager.subscribe');
      return;
    }
    AXES.forEach((axis) => this._subscribeParameter(axis));
    this._subscribeParameter(PREMIX_PARAM);
    this._subscribeParameter(INPUT_GAIN_PARAM);
    this._registerDimensionParameters(this._dimensionIds);
    this._subscriptionsRegistered = true;
  }

  _subscribeParameter(parameterName, dimensionId = null) {
    if (!this.userManager || typeof this.userManager.subscribe !== 'function') return;
    const key = dimensionId ? `${parameterName}::${dimensionId}` : parameterName;
    if (this._subscribedParams.has(key)) return;
    try {
      this.userManager.subscribe(this, parameterName, 2, dimensionId);
      this._subscribedParams.add(key);
    } catch (error) {
      console.warn(`[AudioEngineAdapter] Failed to subscribe to parameter '${parameterName}'`, error);
    }
  }

  _registerDimensionParameters(dimensionIds) {
    if (!dimensionIds) return;
    const list = Array.isArray(dimensionIds) ? dimensionIds : Array.from(dimensionIds);
    list.forEach((dimensionId) => {
      if (!dimensionId) return;
      this._dimensionIds.add(dimensionId);
      AXES.forEach((axis) => {
        this._subscribeParameter(axis, dimensionId);
      });
    });
  }

  onParameterChanged(parameterName, value, dimensionId = null, metadata = null) {
    if (!parameterName) return;

    if (parameterName === PREMIX_PARAM || parameterName === INPUT_GAIN_PARAM) {
      this.setParameter(parameterName, value, null, metadata || {});
      return;
    }

    const axisInfo = this._parseAxisParameterName(parameterName);
    if (axisInfo) {
      const targetDimensionId = dimensionId || axisInfo.dimensionId || this._effectsMeta.activeDimensionId;
      this.setParameter(axisInfo.axis, value, targetDimensionId, metadata || {});
      return;
    }

    if (AXES.includes(parameterName)) {
      this.setParameter(parameterName, value, dimensionId || this._effectsMeta.activeDimensionId, metadata || {});
    }
  }

  addPlaybackStateListener(listener) {
    if (typeof listener !== 'function') {
      return () => {};
    }
    this._playbackStateListeners.add(listener);
    return () => {
      this._playbackStateListeners.delete(listener);
    };
  }

  removePlaybackStateListener(listener) {
    if (!listener) return;
    this._playbackStateListeners.delete(listener);
  }

  getPlaybackState() {
    return this._playbackState;
  }

  getBufferingState() {
    if (typeof this.playback?.getBufferingState === 'function') {
      const state = this.playback.getBufferingState();
      return {
        ...this._bufferingState,
        ...state,
      };
    }
    return { ...this._bufferingState };
  }

  refreshCosmicLfoCache(manager = null) {
    const resolved = manager ?? this._resolveCosmicManager();
    this._cosmicManager = resolved;
    this._cosmicLfoCache = {
      x: resolved?.x ?? null,
      y: resolved?.y ?? null,
      z: resolved?.z ?? null,
    };
  }

  _resolveCosmicManager() {
    return voiceRegistry.getActive()?.cosmicLFOManager ?? null;
  }

  _updatePlaybackState(nextState, { force = false, transient = false, source = 'adapter' } = {}) {
    if (!nextState) return;
    const previousState = this._playbackState;
    const stableStates = new Set(['playing', 'paused', 'stopped']);
    const isStable = stableStates.has(nextState);

    if (!force && !transient && previousState === nextState) {
      return;
    }

    if (isStable) {
      this._playbackState = nextState;
      setPlaybackState(nextState);
      // The drift sampler's lifetime has ONE owner (the same rule the pending transport timers
      // follow). Eligibility — not merely "playing" — starts it: a solo or unsynced voice has no
      // shared phase to hold, and waking it every 500 ms to discover that is exactly the kind of
      // continuous background work this app does not do. Stopping also drops any trim, so a paused
      // voice never resumes on a stale rate.
      this._refreshDriftSampler();
    } else if (force) {
      this._playbackState = nextState;
    }

    const payload = {
      state: nextState,
      previousState,
      stable: isStable,
      transient: Boolean(transient),
      source,
      timestamp: typeof performance !== 'undefined' ? performance.now() : Date.now(),
    };

    this._playbackStateListeners.forEach((listener) => {
      try {
        listener(payload);
      } catch (error) {
        console.error('[AudioEngineAdapter] playback state listener error', error);
      }
    });
  }


  _setDimensionOrder(order = []) {
    const filtered = Array.isArray(order)
      ? order.filter((id) => typeof id === 'string' && id.length)
      : [];
    const existing = this._dimensionOrder.filter((id) => !filtered.includes(id));
    const nextOrder = [...filtered, ...existing];

    const sameLength = nextOrder.length === this._dimensionOrder.length;
    const sameOrder = sameLength && nextOrder.every((id, i) => id === this._dimensionOrder[i]);

    if (!sameOrder) {
      this._dimensionOrder = nextOrder;
      this._dimensionChains.forEach((_, dimensionId) => {
        if (!this._dimensionOrder.includes(dimensionId)) {
          this._dimensionOrder.push(dimensionId);
        }
      });
      this._rewireGlobalChain();
      return;
    }

    // Ensure any new chains are included without forcing a rewire if order stayed the same
    let pushed = false;
    this._dimensionChains.forEach((_, dimensionId) => {
      if (!this._dimensionOrder.includes(dimensionId)) {
        this._dimensionOrder.push(dimensionId);
        pushed = true;
      }
    });
    if (pushed) {
      this._rewireGlobalChain();
    }
  }

  /**
   * Point the source stage (`normalizationGain → inputLevelGain`) at whatever comes next — the
   * first effects chain, or the premix fader when there are none.
   *
   * The downstream edge is dropped BY TARGET rather than with a blanket `disconnect()`: the
   * input level tap is an outgoing branch of `inputLevelGain`, so clearing every edge would
   * take the analyser with it and the input meter would go dead the first time the effects
   * chain was rebuilt. `normalizationGain` has no such branch (source engines connect INTO
   * it), so it can still be cleared wholesale.
   */
  _connectSourceStageTo(target) {
    if (!target || !this.normalizationGain) return;
    try { this.normalizationGain.disconnect(); } catch (_) {}
    const stage = this.inputLevelGain;
    if (!stage) {
      this.normalizationGain.connect(target);
      return;
    }
    this.normalizationGain.connect(stage);
    if (this._inputStageTarget === target) return;
    if (this._inputStageTarget) {
      try { stage.disconnect(this._inputStageTarget); } catch (_) {}
    }
    stage.connect(target);
    this._inputStageTarget = target;
  }

  _rewireGlobalChain() {
    const player = this.playback?.player;
    const hasPlayerSource = Boolean(player && typeof player.connect === 'function');

    if (hasPlayerSource) {
      try {
        player.disconnect();
      } catch (_) {}
    }

    // Disconnect previous chain outputs (keep per-rack internal wiring intact).
    this._dimensionChains.forEach((chain) => {
      try { chain.outputGain.disconnect(); } catch (_) {}
    });

    // Teardown above always runs; only the re-connect below needs somewhere to connect TO.
    //
    // The master bus is built when a session loads. If the load failed there is nothing to
    // wire into, and every later parameter change would otherwise throw on a null
    // destination — the engine must stay inert until a session actually arrives, not fail
    // loudly once per knob. Placed after the disconnects so a bus torn down mid-session still
    // gets its stale edges cleared rather than left feeding a dead graph.
    if (!this.bodyLevelGain && !this.masterGain) {
      return;
    }

    const orderedChains = this._dimensionOrder
      .map((id) => this._dimensionChains.get(id))
      .filter(Boolean);

    if (!orderedChains.length) {
      // No effects: [source] → normalizationGain → inputLevelGain → bodyLevelGain.
      // The source stage is wired REGARDLESS of a Tone player, because the streaming backend joins
      // it through setInputNode instead of being connected here.
      try {
        this._connectSourceStageTo(this.bodyLevelGain || this.masterGain);
        if (hasPlayerSource) {
          if (this.normalizationGain) player.connect(this.normalizationGain);
          else player.connect(this.inputLevelGain || this.bodyLevelGain || this.masterGain);
        }
      } catch (error) {
        console.warn('[AudioEngineAdapter] Failed to connect player to master', error);
      }
      return;
    }

    // Keep dimension chain outputs connected to master regardless of playback source type.
    for (let i = 0; i < orderedChains.length - 1; i += 1) {
      const current = orderedChains[i];
      const next = orderedChains[i + 1];
      try {
        current.outputGain.connect(next.inputGain);
      } catch (error) {
        console.warn('[AudioEngineAdapter] Failed to connect dimension chain output', {
          fromDimensionId: current.dimensionId,
          toDimensionId: next.dimensionId,
          error,
        });
      }
    }

    const lastChain = orderedChains[orderedChains.length - 1];
    try {
      // Connect effects output to bodyLevelGain (master fader)
      const outputTarget = this.bodyLevelGain || this.masterGain;
      lastChain.outputGain.connect(outputTarget);
    } catch (error) {
      console.warn('[AudioEngineAdapter] Failed to connect final chain to master', error);
    }

    // Source wiring: [source] → normalizationGain → inputLevelGain → first effect → … → bodyLevelGain.
    // The source stage is wired REGARDLESS of a Tone player (the streaming backend joins it through
    // setInputNode), so the input trim and its meter are in the path for every playback strategy.
    {
      try {
        this._connectSourceStageTo(orderedChains[0].inputGain);
        if (hasPlayerSource) {
          if (this.normalizationGain) player.connect(this.normalizationGain);
          else player.connect(this.inputLevelGain || orderedChains[0].inputGain);
        }
      } catch (error) {
        console.warn('[AudioEngineAdapter] Failed to connect player to first dimension chain', {
          dimensionId: orderedChains[0]?.dimensionId,
          error,
        });
      }
    }
  }
}

export default AudioEngineAdapter;
